import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';

import {
  EntryRule,
  UpdateEntryRuleDto,
  ManualConfirmedPair,
  ENTRY_HARD_CAP_GROSS,
  EnrichedEntryStatus,
  MakerFlag
} from '../../types/entry.types';
import {
  useSetPairOpenerEnabled,
  useBulkSetPairOpenerEnabled,
  useBulkUpdateEntryRuleParams,
  useSetAllowThresholdAboveOne,
  useSetRuleArmed
} from '../../api/useEntry';
import { useSubgroups, useBulkAssignPairSubgroup } from '../../api/useSubgroups';
import {
  UNASSIGN_SUBGROUP_VALUE,
  buildBulkAssignSubgroupPayload
} from './subgroupAssign';
import { Comparator, LiveStore } from '../../live/createLiveStore';
import { waitForLiveSettle } from '../../live/waitForLiveSettle';
import { useLiveOrder, useLiveState } from '../../live/useLiveStore';
import {
  ActNetCell,
  VENUE_ACCOUNT_WIDE_NOTE,
  VENUE_RESOLVING_NOTE,
  VENUE_UNAVAILABLE_LABEL,
  VENUE_WARN_COLOR,
  restUnhedgeMagnitude,
  venueUnavailableColor,
  type RestNetSource,
} from '../../live/NetCell';
import {
  resolvePairVenueView,
  restStaleVenueNote,
  rowAccountScope,
  venueSortMagnitude,
  type GroupAccountIndex,
  type PairVenueView,
  type VenueStatusByScope,
  type VenueUnavailableReason,
  type VenueViewStatus,
  type ViewAccountScope,
} from '../../live/venuePositions';
import { usePositionsGrouped } from '../../api/usePositions';
import { BulkActionBar } from '../BulkActionBar';
import {
  KALSHI_TAKER_FEE_RATE,
  polymarketTakerFeeRate,
  takerTnaFromSum,
  fmtPrice,
  fmtQty,
  fmtPct,
  TAB_FLAGS,
  ENTRY_THRESHOLD_CAP_MODE_HEDGE,
  isThresholdWithinBound,
  resolveEffectiveHedgeMode,
  thresholdBound,
  thresholdInputMax
} from './entryMath';
import { GroupMakerBulkControls } from './GroupMakerBulkControls';
import { GroupArmBulkControls } from './GroupArmBulkControls';
import {
  UNGROUPED_ACTIVATION_TOOLTIP,
  isActivationBlocked,
  needsOpenerAutoEnable
} from './activationGate';
import { resolveMakerSwitchState } from './makerSwitchState';
import { resolveArmSwitchState, resolveWsArmed } from './armSwitchState';
import { groupSelectionKey, type GroupSelection } from './groupFilter';
import {
  BULK_ENABLE_PAIR_IDS_CHUNK,
  applyBulkParamsDtos,
  buildBulkEnableAllPayloads,
  buildBulkParamsDtos,
  bulkAllConfirmCount,
  bulkAllTargetIds,
  chunkIds,
  type BulkParamSelection,
  type BulkParamsValues
} from './bulkScope';
import {
  BULK_ENABLE_CONCURRENCY,
  BulkPartialFailureError,
  aggregateBulkCounts,
  collectUnconfirmedIds,
  runWithConcurrency,
  unconfirmedBulkMessage,
  type BulkWriteCounts
} from '../shared/bulkConfirm';
import { buildRulesByPairId } from '../../utils/rulesByPairId';
import {
  INHERIT_SENTINEL,
  buildPendingModeFields,
  fromSelectValue,
  hedgeModeOptions,
  shortLabelFor,
  toSelectValue,
  type HedgeModeSelectValue
} from '../shared/counterHedgeMode';
import {
  GlobalGateToggles,
  HedgeModeMaxSumControls,
  SweepConfigButton
} from '../shared/HedgeModeGlobalControls';
import { parseWalkInput, isWalkFieldDirty } from '../shared/walkParams';
import { useSystemConfig } from '../../api/useSystemConfig';
import { soccerMarketRank } from '../../constants/soccerMarketOrder';

interface PairContracts {
  polyYes: number;
  polyNo: number;
  kalshiYes: number;
  kalshiNo: number;
}

const ZERO_CONTRACTS: PairContracts = {
  polyYes: 0,
  polyNo: 0,
  kalshiYes: 0,
  kalshiNo: 0
};

/**
 * The counts the YES / NO cells actually render, tagged with where they came
 * from: `venue` = the group's own feed pod polled the venue under the group's
 * account; `rest` = the main-account-only `prediction_positions` map; `none` =
 * neither has anything; `unavailable` = we have no account-correct read, so the
 * cells render `—` beside the ACT `—` instead of a main-account number that is
 * not this group's.
 */
interface PairContractsView extends PairContracts {
  source: 'venue' | 'rest' | 'none' | 'unavailable';
  /** Set with `source: 'unavailable'` — drives both the colour and the hover. */
  unavailableReason?: VenueUnavailableReason;
  /** Venue counts whose imbalance is a settled leg awaiting redemption. */
  resolving?: boolean;
}

const hasAnyContracts = (c: PairContracts): boolean =>
  c.polyYes > 0 || c.polyNo > 0 || c.kalshiYes > 0 || c.kalshiNo > 0;

/**
 * Kalshi reports a single SIGNED net per ticker, so it structurally cannot
 * express holding YES and NO on the same market — at most one of the two K rows
 * can be non-zero. Spelling that out stops the operator reading the always-blank
 * side as lost data.
 */
const KALSHI_NET_ONLY_NOTE =
  'Kalshi reports one SIGNED net per ticker, so only one of the two K rows ' +
  '(YES or NO) can ever be non-zero for a market — a blank K on the other ' +
  'side is the wire format, not missing data.';

const REST_CONTRACTS_NOTE =
  'MAIN-account contracts (the prediction_positions REST sync), NOT a venue ' +
  'read of this group’s account.';

/** Where the YES / NO numbers came from, on the row's own hover. */
const contractsNote = (
  view: PairContractsView,
  status: VenueViewStatus
): string => {
  if (view.source === 'venue') {
    return (
      `Venue read · ${VENUE_ACCOUNT_WIDE_NOTE} ${KALSHI_NET_ONLY_NOTE}` +
      (view.resolving ? ` ${VENUE_RESOLVING_NOTE}` : '')
    );
  }
  if (view.source === 'unavailable') {
    const label = VENUE_UNAVAILABLE_LABEL[view.unavailableReason ?? 'no-read'];
    return `No account-correct contract count — ${label}. Showing nothing rather than the main account’s numbers.`;
  }
  return (
    `${REST_CONTRACTS_NOTE} ${KALSHI_NET_ONLY_NOTE}` +
    restStaleVenueNote(status)
  );
};

/**
 * Colour by (source × platform): a NON-ZERO venue count and a non-zero REST
 * count never land in the same shade, so the provenance encoding stays legible
 * instead of decorative — venue = bright greys, REST = a blue tint. A zero has
 * no source to attribute (it renders as `—`) and takes the neutral grey either
 * way. `resolving` venue counts and an unavailable cell take the same colours
 * the ACT cell gives those states, so the three cells never disagree about how
 * bad the row is.
 */
const contractsColor = (
  view: PairContractsView,
  platform: 'poly' | 'kalshi',
  value: number
): string => {
  if (view.source === 'unavailable') {
    return venueUnavailableColor(view.unavailableReason ?? 'no-read');
  }
  if (!value) return 'grey.600';
  if (view.source === 'venue') {
    if (view.resolving) return VENUE_WARN_COLOR;
    return platform === 'poly' ? 'grey.100' : 'grey.400';
  }
  return platform === 'poly' ? '#8ab4e8' : '#5f86b8';
};

const fmtContracts = (v: number): string => {
  if (!v) return '—';
  return Math.round(v).toString();
};

/** The YES / NO counts for one row, from the SAME view the ACT cell renders. */
const contractsViewFor = (
  view: PairVenueView,
  rest: PairContracts
): PairContractsView => {
  if (view.kind === 'venue') {
    return { ...view.venue, source: 'venue', resolving: view.resolving };
  }
  if (view.kind === 'unavailable') {
    return {
      ...ZERO_CONTRACTS,
      source: 'unavailable',
      unavailableReason: view.reason
    };
  }
  return { ...rest, source: hasAnyContracts(rest) ? 'rest' : 'none' };
};

// Subtle "unsaved draft" accent for a Parameters-group input: when the
// field is dirty (its value differs from the last committed one) the input
// outline turns blue so the operator can see at a glance which fields are
// staged but not yet confirmed via the per-row ✓. Returns the base sx
// untouched when clean.
function dirtyInputSx(
  baseSx: Record<string, unknown>,
  dirty: boolean
): Record<string, unknown> {
  if (!dirty) return baseSx;
  return {
    ...baseSx,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#42a5f5'
    }
  };
}

const hSx = {
  fontSize: '0.58rem',
  fontWeight: 600,
  color: 'grey.500',
  py: 0.4,
  px: 0.5,
  lineHeight: 1.2,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.02em',
  whiteSpace: 'nowrap' as const,
  borderBottom: '1px solid rgba(255,255,255,0.06)'
};

// Sticky offset for the second header row. Must equal row 1's actual
// rendered height in pixels, otherwise the second row either covers
// part of row 1 (overlap) or floats over the body (gap → first body
// row scrolls behind it). With `py: 0.4` + fontSize 0.58rem +
// lineHeight 1.2 + 1px border the row is ~21px.
const ROW1_HEIGHT = 21;

// Second-header-row cells get extra bottom padding + a stronger
// divider so there's visible breathing room between the sub-header
// and the first body row. Bump pb if the operator reports the body
// row 1 still looks crowded against the sub-header.
const subHSx = {
  pb: 1.2,
  borderBottom: '2px solid rgba(255,255,255,0.18)'
};
const gSx = {
  ...hSx,
  textAlign: 'center' as const,
  borderBottom: '1px solid rgba(255,255,255,0.1)'
};
const cSx = { fontSize: '0.72rem', py: 0.15, px: 0.5 } as const;
// Sim cells only: tighter horizontal padding so the legs / $-% numbers get the
// full column width (the ✓ → TT gap was mostly cell padding).
const simCSx = { ...cSx, px: 0.25 } as const;

export interface CreateEntrySumRuleParams {
  pair: ManualConfirmedPair;
  /**
   * Which of the 4 maker flags this row activated on. Drives the initial
   * sides only — the flag is NEVER baked into the create (rules are born
   * paused, all flags off); the caller applies it via PATCH afterwards so
   * the false→true activation edge fires. Null when activation came from
   * TT or a pre-rule paused-create via the param ✓.
   */
  makerFlag: MakerFlag | null;
  /**
   * Path of the tab the activation happened in. Drives the rule's poly /
   * kalshi sides when `makerFlag` is null (TT-only or paused-create from
   * params). Ignored when `makerFlag` is set — sides derive from the flag
   * via FLAG_TO_SIDES.
   */
  tabPath: 'a' | 'b';
  thresholdSum: number;
  size: number;
  maxVolumeContracts: number;
  makerSizeMode?: 'dynamic' | 'fixed';
  counterHedgeMode?:
    | 'always-threshold'
    | 'gtc-to-max-sum'
    | 'market-to-hedge'
    | null;
  counterStuckMarketHedgeSeconds?: number | null;
  counterMakerWalkStepSeconds?: number | null;
  counterMakerWalkStepCents?: number | null;
  lockstepMaxAheadBatches?: number | null;
  disablePlacementGatePolymarket?: boolean | null;
  disablePlacementGateKalshi?: boolean | null;
  sweepDualPostEnabled?: boolean;
  /** Per-pair "> $1" opt-in, staged in the row draft and persisted at create
   *  (the backend spreads it onto the new paused rule). */
  allowThresholdAboveOne?: boolean;
}

interface Props {
  /**
   * Which entry path this tab governs. Tab A = poly YES + kalshi NO;
   * Tab B = poly NO + kalshi YES.
   */
  path: 'a' | 'b';
  pairs: ManualConfirmedPair[];
  /** Active group filter — `pairs` is already narrowed by it; bulk actions
   *  use it to pick the matching server-side scope. */
  groupSelection: GroupSelection;
  /** Display name of the active group filter (null = All groups). */
  groupLabel: string | null;
  /** Concrete subgroup id when a single subgroup is selected within the
   *  group, else null. Scopes the bulk-params bar to that subgroup via the
   *  server-side `subgroupId` narrow (mirrors the subgroup maker-flag flip). */
  subgroupId: string | null;
  /** Display name of the selected subgroup (null when none). Quoted in the
   *  bulk-params bar's "Apply to {subgroup}" button + confirm. */
  subgroupLabel: string | null;
  /** Group-scoped pair set BEFORE the page search narrows it — what a
   *  server-side "All ON/OFF" (`scope:'all'` [+ groupId]) actually flips.
   *  Feeds the confirm dialog's honest total AND the bulk-params bar's
   *  scope:'ids' rule-id set, so "Apply to {group}" covers the whole
   *  group exactly like "All ON/OFF" does. */
  groupedPairs: ManualConfirmedPair[];
  rules: EntryRule[] | undefined;
  anyRulesByPairId: Map<string, EntryRule>;
  /** Per-pair external store for the live price/sim state. Rows subscribe
   *  per-key; the tab subscribes to the throttled sorted-id order. */
  store: LiveStore<EnrichedEntryStatus>;
  /** Which source the ACT / YES / NO cells may render, resolved from the
   *  socket's venue meta once per ACCOUNT SCOPE. Each row picks the entry for
   *  its own group — the table is not single-account under "All groups". */
  venueStatusByScope: VenueStatusByScope;
  /** Account each opener group trades under; unlisted groups are unresolved. */
  groupAccounts: GroupAccountIndex;
  onCreateRule: CreateEntryRuleFn;
  onSetMakerFlag: (
    rule: EntryRule,
    flag: MakerFlag,
    value: boolean,
    clearOthers?: MakerFlag[]
  ) => Promise<void> | void;
  onUpdateRule: (rule: EntryRule, dto: UpdateEntryRuleDto) => Promise<boolean>;
  onHidePair: (pair: ManualConfirmedPair) => Promise<void> | void;
}

/**
 * Creates the pair's rule and resolves to it, or null when a page-level
 * guard blocked the create. Single-flight per pair — concurrent calls join
 * the same POST. Maker/strategy flags are applied via PATCH afterwards,
 * never baked into the create.
 */
type CreateEntryRuleFn = (
  params: CreateEntrySumRuleParams
) => Promise<EntryRule | null>;

const DEFAULT_THRESHOLD = 0.95;
const DEFAULT_SIZE = 50;
const DEFAULT_MAX_VOL = 500;

// Proportional column widths fed to a <colgroup>. Same pattern as the
// unwinder's Sum-of-Sides table (see `UnwindingTabSumOfSides.tsx`): the
// Table uses `tableLayout: 'fixed'` + `width: '100%'`, and the colgroup
// renders each pixel width as a percentage of the total so it is
// authoritative AND the table stretches to fill a wider container
// instead of rendering at its ~1384 px natural size and leaving the
// rest of the screen unused. Order matches the rendered column
// sequence: checkbox · Market · YES · NO · ACT · Polymarket (Qty/Bid/Ask/Qty)
// · Kalshi (Qty/Bid/Ask/Qty) · Parameters (Thr/Size/MaxV/Mode/Hedge/Wait/
// Gate/Sweep/Walk-s/Walk-¢/✓) · 3 strategy groups (Legs + $/% each) · Ctrl
// (P/K/TT).
// Sequence: …Thr(44), Size(40), MaxV(46), Mode(50), Hedge(60), Wait(34),
// Gate(34), Sweep(40), Walk-s(48), Walk-¢(48), ✓(28), then the 3 strategy
// groups (Legs/$%), then Ctrl P/K/TT. Market is the WIDEST column (342) — the
// long market titles must read in full, so EVERY pixel reclaimed elsewhere is
// fed back here. ACT trimmed (36) and Bid/Ask trimmed (37 each) to shave a
// little margin without colliding the two prices. Thr/Size/MaxV kept (44/40/46).
// The Wait/Gate checkbox columns sit just wide enough to keep their header
// titles legible. Thr fits a 5-char threshold (e.g. 0.955); Walk columns 48 each;
// the ✓ confirm column is 22 (small check icon, tight right margin) and sits
// right after Walk to batch-commit the row's staged param edits. The sim columns
// (legs 74 / $-% 54, tighter px via simCSx) are sized so the widest contents —
// a maker leg's `bid / place` pair and a long negative TNA — fit without
// bleeding into the neighbour cell. The three Ctrl
// toggle columns are 42 each: tight enough that the MUI `size="small"` Switch
// (~40 px intrinsic) fits with only a sliver of separation — they were 28
// (clipped+crammed → horizontal scroll), then 60 (too much gap), then 50 (still
// too much gap on the operator's wide monitor). Every px shaved off the toggles
// is fed back to Market (title) — see the "don't shrink titles" note below.
const COL_WIDTHS_PX = [
  18, 366, 50, 50, 36, 40, 37, 37, 32, 24, 37, 37, 32, 44, 40, 46, 50, 60, 34,
  34, 40, 48, 48, 22,
  74, 54, 74, 54, 74, 54, 42, 42, 42
] as const;
const COL_TOTAL_PX = COL_WIDTHS_PX.reduce((s, w) => s + w, 0);

// Ctrl P/K/TT switches. A bare `size="small"` Switch renders its checked
// state as the theme primary (#5141CB) at ~0.5 opacity, which on the near-black
// row background is a dim purple almost indistinguishable from the gray "off"
// track — enabled toggles read as off ("no se ven"). Force the ON state to a
// solid, opaque purple + white thumb (matching the arm switch), so an enabled
// maker/TT toggle is unmistakable.
const CTRL_SWITCH_SX = {
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#fff',
    '& + .MuiSwitch-track': {
      backgroundColor: '#7c3aed',
      opacity: 1
    }
  },
  '& .MuiSwitch-track': {
    backgroundColor: 'rgba(255,255,255,0.18)',
    opacity: 1
  }
} as const;

// THR input accent when the typed value crosses $1 on an enabled pair — amber +
// bold reads as "this is an above-$1 threshold" without any wrapping advisory.
const ABOVE_ONE_THR_SX = {
  '& .MuiOutlinedInput-input': { color: '#ffb300', fontWeight: 700 },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,179,0,0.6)'
  }
} as const;

type SortKey =
  // Resting/default order: NOT tied to a column header. Deterministic
  // composite — market-data-enabled tier first, then grouped by event
  // link (the pair's marketUrl) so every market of the same event sits
  // together (e.g. all F1 "pole position" rows, then all "race winner"),
  // then A→Z by name within each link group. This is what the table
  // shows on mount so the row order never jitters on a price tick and is
  // identical across reconnects. The operator opts into a live metric
  // sort by clicking a column header.
  | 'default'
  | 'name'
  // Soccer market-type bucket order (moneyline → spread → … → other), per the
  // canonical FE order map in `constants/soccerMarketOrder.ts`. Unset/unknown
  // buckets (incl. all non-soccer pairs) sink to the bottom.
  | 'marketType'
  // Absolute size of the actual leg imbalance (`|YES − NO|`) read off the held
  // REST positions — surfaces the largest naked positions first so the operator
  // can hedge them manually. Sign-agnostic: an excess-YES and excess-NO of the
  // same size rank together. Independent of market-data state, so this sort
  // ignores the live tiers below.
  | 'unhedge'
  | 'sumTT'
  | 'tnaTT'
  | 'sumPolyMk'
  | 'tnaPolyMk'
  | 'sumKalshiMk'
  | 'tnaKalshiMk';
type SortDir = 'asc' | 'desc';

// Sums sort ascending by default (cheapest combined cost first); TNAs
// sort descending by default (highest annualized return first).
const SUM_KEYS: SortKey[] = ['sumTT', 'sumPolyMk', 'sumKalshiMk'];

/* ------------------------------------------------------------------ */
/* Math helpers — per-pair                                            */
/* ------------------------------------------------------------------ */

interface PathMetrics {
  polyAsk: number | null;
  polyBid: number | null;
  kalshiAsk: number | null;
  kalshiBid: number | null;
  polyAllIn: number | null;
  kalshiAllIn: number | null;
  polyMakerRest: number | null;
  kalshiMakerRest: number | null;
  polyMakerPlace: number | null;
  kalshiMakerPlace: number | null;
  sumTT: number | null;
  sumPolyMk: number | null;
  sumKalshiMk: number | null;
  tnaTT: number | null;
  tnaPolyMk: number | null;
  tnaKalshiMk: number | null;
}

const NULL_PATH_METRICS: PathMetrics = {
  polyAsk: null,
  polyBid: null,
  kalshiAsk: null,
  kalshiBid: null,
  polyAllIn: null,
  kalshiAllIn: null,
  polyMakerRest: null,
  kalshiMakerRest: null,
  polyMakerPlace: null,
  kalshiMakerPlace: null,
  sumTT: null,
  sumPolyMk: null,
  sumKalshiMk: null,
  tnaTT: null,
  tnaPolyMk: null,
  tnaKalshiMk: null
};

const computePathMetrics = (
  path: 'a' | 'b',
  liveState: EnrichedEntryStatus | undefined
): PathMetrics => {
  // When the per-pair opener kill switch is off (`pairEnabled !== true`)
  // the opener is bringing NO data for this pair. Every column —
  // raw market quotes AND the engineSim-derived sums — must collapse to
  // "—": rendering the last (now stale) numbers would lie. Mirrors the
  // unwinder's `isMonitored` gate in UnwindingTabSumOfSides.
  if (liveState?.pairEnabled !== true) {
    return NULL_PATH_METRICS;
  }
  // Raw top-of-book bid/ask for the tab's path (rendered in the
  // platform Bid/Ask cells). Backend WS quotes from the MarketState.
  const polyAsk =
    (path === 'a' ? liveState?.polyYesAsk : liveState?.polyNoAsk)?.price ??
    null;
  const polyBid =
    (path === 'a' ? liveState?.polyYesBid : liveState?.polyNoBid)?.price ??
    null;
  const kalshiAsk =
    (path === 'a' ? liveState?.kalshiNoAsk : liveState?.kalshiYesAsk)?.price ??
    null;
  const kalshiBid =
    (path === 'a' ? liveState?.kalshiNoBid : liveState?.kalshiYesBid)?.price ??
    null;

  // Strategy sums, maker rest, and all-in taker prices ALL come from the
  // backend `engineSim` payload — the SAME helpers the engine triggers
  // orders on (depth-walked VWAPs + findBestMakerPrice). There is NO
  // client-side fallback: the FE must never approximate these numbers.
  // When `engineSim` is absent (backend has no orderbook for the pair)
  // every sim field is null and the cell renders "—".
  const engine = liveState?.engineSim?.[path];
  const polyAllIn = engine?.polyTakerAllInVwap ?? null;
  const kalshiAllIn = engine?.kalshiTakerAllInVwap ?? null;
  const polyMakerRest = engine?.polyMakerRest ?? null;
  const kalshiMakerRest = engine?.kalshiMakerRest ?? null;
  const polyMakerPlace = engine?.polyMakerPlace ?? null;
  const kalshiMakerPlace = engine?.kalshiMakerPlace ?? null;
  const sumTT = engine?.sumTT ?? null;
  const sumPolyMk = engine?.sumPolyMk ?? null;
  const sumKalshiMk = engine?.sumKalshiMk ?? null;

  const days = liveState?.daysToExpiry ?? null;
  return {
    polyAsk,
    polyBid,
    kalshiAsk,
    kalshiBid,
    polyAllIn,
    kalshiAllIn,
    polyMakerRest,
    kalshiMakerRest,
    polyMakerPlace,
    kalshiMakerPlace,
    sumTT,
    sumPolyMk,
    sumKalshiMk,
    tnaTT: takerTnaFromSum(sumTT, days),
    tnaPolyMk: takerTnaFromSum(sumPolyMk, days),
    tnaKalshiMk: takerTnaFromSum(sumKalshiMk, days)
  };
};

// The live sort metric for one pair under a given sort key. Mirrors the
// per-key switch the old `sortedPairs` annotation used. `null` for the
// resting/name sorts (those order by name, not a metric).
const metricForSort = (
  metrics: PathMetrics,
  sortKey: SortKey
): number | null => {
  switch (sortKey) {
    case 'sumTT':
      return metrics.sumTT;
    case 'tnaTT':
      return metrics.tnaTT;
    case 'sumPolyMk':
      return metrics.sumPolyMk;
    case 'tnaPolyMk':
      return metrics.tnaPolyMk;
    case 'sumKalshiMk':
      return metrics.sumKalshiMk;
    case 'tnaKalshiMk':
      return metrics.tnaKalshiMk;
    default:
      return null;
  }
};

/* ------------------------------------------------------------------ */
/* Table                                                              */
/* ------------------------------------------------------------------ */

export const EntryTabByPath = ({
  path,
  pairs,
  groupSelection,
  groupLabel,
  subgroupId,
  subgroupLabel,
  groupedPairs,
  rules,
  anyRulesByPairId,
  store,
  venueStatusByScope,
  groupAccounts,
  onCreateRule,
  onSetMakerFlag,
  onUpdateRule,
  onHidePair
}: Props) => {
  // Resting sort is the Market column ('name', A→Z) so the table opens with
  // the Market header showing as the active default sort, in a stable order
  // that stays put across price ticks and WS reconnects. Clicking another
  // column header switches to that column's live metric sort.
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Freeze-sort: when on, the live store holds the current sorted order so
  // price ticks stop reshuffling the table (cells keep ticking). The store
  // owns the held order internally via `setOrderInputs({ freeze })`; new
  // pairs that appear while frozen tail-sort by the comparator off the
  // store's distinct-id set.
  const [frozen, setFrozen] = useState(false);

  const rulesByPairId = useMemo(() => buildRulesByPairId(rules), [rules]);

  // Event-link grouping key per store key, for the resting ('default')
  // sort. The pair's marketUrl is the "link" that ties every market of the
  // same event together (Polymarket event slug preferred, Kalshi as
  // fallback — same precedence the Market cell uses for the row's links).
  // Dual-keyed by pairId AND ruleId because the store's comparator receives
  // whichever the store ranked the row under. Read off the pair (not the
  // live snapshot) so the group is stable even before a WS envelope arrives.
  const linkByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const pair of pairs) {
      const link = pair.polymarket?.marketUrl ?? pair.kalshi?.marketUrl ?? '';
      if (pair._id) map.set(pair._id, link);
      const rule = pair._id ? rulesByPairId.get(pair._id) : undefined;
      if (rule?._id) map.set(rule._id, link);
    }
    return map;
  }, [pairs, rulesByPairId]);

  // Soccer market-type bucket per store key, for the 'marketType' sort.
  // Dual-keyed by pairId AND ruleId (the store's comparator receives
  // whichever the store ranked the row under — mirrors `linkByKey`). Read
  // off the pair's classified `marketType` (unset for non-soccer pairs).
  const marketTypeByKey = useMemo(() => {
    const map = new Map<string, string | null | undefined>();
    for (const pair of pairs) {
      const bucket = pair.marketType;
      if (pair._id) map.set(pair._id, bucket);
      const rule = pair._id ? rulesByPairId.get(pair._id) : undefined;
      if (rule?._id) map.set(rule._id, bucket);
    }
    return map;
  }, [pairs, rulesByPairId]);

  // Current open contracts split by (platform × side) per pair.
  // Mirrors the unwinder (UnwindingPage.tsx) which uses the same REST
  // hook; the /opening route is NOT wrapped by PositionsProvider, so
  // the context-based usePositions() throws here. 10s poll matches the
  // unwinder's freshness budget.
  const { data: positionsData } = usePositionsGrouped({ status: 'open' });
  const contractsByPairId = useMemo(() => {
    const map = new Map<string, PairContracts>();
    const groups = positionsData?.groups ?? [];
    for (const g of groups) {
      if (!g._id) continue;
      let polyYes = 0;
      let polyNo = 0;
      let kalshiYes = 0;
      let kalshiNo = 0;
      for (const p of g.positions) {
        if (p.status !== 'open' || !p.contracts) continue;
        if (p.platform === 'polymarket') {
          if (p.side === 'yes') polyYes += p.contracts;
          else polyNo += p.contracts;
        } else if (p.platform === 'kalshi') {
          if (p.side === 'yes') kalshiYes += p.contracts;
          else kalshiNo += p.contracts;
        }
      }
      map.set(g._id, { polyYes, polyNo, kalshiYes, kalshiNo });
    }
    return map;
  }, [positionsData]);

  // Absolute unhedge size per pair (`|total YES − total NO|`) for the ACT-column
  // sort, dual-keyed by pairId AND ruleId (the store's comparator receives
  // whichever the store ranked the row under — mirrors `linkByKey`). This is the
  // REST tier of the sort; the comparator prefers each row's live
  // `venuePositions` when the group's venue meta is fresh, matching whichever
  // source the ACT cell renders.
  const unhedgeByKey = useMemo(() => {
    const map = new Map<string, number>();
    for (const pair of pairs) {
      if (!pair._id) continue;
      const c = contractsByPairId.get(pair._id) ?? ZERO_CONTRACTS;
      const mag = restUnhedgeMagnitude({
        positions: [
          { platform: 'polymarket', side: 'yes', contracts: c.polyYes },
          { platform: 'polymarket', side: 'no', contracts: c.polyNo },
          { platform: 'kalshi', side: 'yes', contracts: c.kalshiYes },
          { platform: 'kalshi', side: 'no', contracts: c.kalshiNo },
        ],
      });
      map.set(pair._id, mag);
      const rule = rulesByPairId.get(pair._id);
      if (rule?._id) map.set(rule._id, mag);
    }
    return map;
  }, [pairs, contractsByPairId, rulesByPairId]);

  // Account scope per store key, so the ACT sort ranks each row under the same
  // status its cells render — a test-group row must not be ordered by the
  // main-account magnitude it is refusing to show.
  const scopeByKey = useMemo(() => {
    const map = new Map<string, ViewAccountScope>();
    for (const pair of pairs) {
      if (!pair._id) continue;
      const scope = rowAccountScope(pair.openerGroupId, groupAccounts);
      map.set(pair._id, scope);
      const rule = rulesByPairId.get(pair._id);
      if (rule?._id) map.set(rule._id, scope);
    }
    return map;
  }, [pairs, rulesByPairId, groupAccounts]);

  const handleSortClick = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
      } else {
        setSortKey(key);
        // Sums: ascending default (cheapest first). TNAs: descending
        // default (highest yield first). Name: ascending.
        if (key === 'name') setSortDir('asc');
        else if (key === 'marketType') setSortDir('asc');
        else if (SUM_KEYS.includes(key)) setSortDir('asc');
        else setSortDir('desc');
      }
    },
    [sortKey]
  );

  // ── Store-driven sort order ──────────────────────────────────────────
  // The high-frequency live state lives in the external store; the sorted
  // order recomputes on the store's throttled, rAF-coalesced cadence (not
  // on every WS tick / parent render). The comparator below operates on
  // the store's keys (pairId/ruleId) and reads each pair's live state via
  // `getKeySnapshot` — the SAME tier logic the old `sortedPairs` used:
  // market-data-enabled first, then default/name/metric within the tier.
  const comparator = useMemo<Comparator>(() => {
    return (ka, kb) => {
      const la = store.getKeySnapshot(ka);
      const lb = store.getKeySnapshot(kb);

      // ACT sort: order by the absolute held imbalance (`|YES − NO|`) so the
      // largest naked positions lead, BEFORE the market-data tier — a naked leg
      // still needs hedging even with the pair's market data turned off. Ties
      // (incl. the all-zero / fully-hedged majority) fall back to A→Z by name.
      if (sortKey === 'unhedge') {
        // Rank each row off the SAME view its ACT cell renders — a row showing
        // `—` must never be ordered by a main-account REST magnitude it isn't
        // displaying. Unknown rows rank `Infinity` (they lead the
        // largest-naked-first order rather than sinking with the flat ones).
        const magOf = (
          key: string,
          live: EnrichedEntryStatus | undefined
        ): number =>
          venueSortMagnitude(
            resolvePairVenueView(
              venueStatusByScope[scopeByKey.get(key) ?? 'unknown'],
              live?.venuePositions
            ),
            unhedgeByKey.get(key) ?? 0
          );
        const ua = magOf(ka, la);
        const ub = magOf(kb, lb);
        if (ua === ub) {
          return (la?.pairName ?? '').localeCompare(lb?.pairName ?? '');
        }
        return sortDir === 'desc' ? ub - ua : ua - ub;
      }

      const enA = la?.pairEnabled === true;
      const enB = lb?.pairEnabled === true;
      // Tier 1: market-data-ENABLED pairs first (every sort).
      if (enA !== enB) return enA ? -1 : 1;

      const nameA = la?.pairName ?? '';
      const nameB = lb?.pairName ?? '';

      // Resting order: group by event link (marketUrl) within the enabled
      // tier, then A→Z by name inside each link group. Pairs with no link
      // sink to the bottom of the tier so the named groups lead.
      if (sortKey === 'default') {
        const linkA = linkByKey.get(ka) ?? '';
        const linkB = linkByKey.get(kb) ?? '';
        if (linkA !== linkB) {
          if (!linkA) return 1;
          if (!linkB) return -1;
          return linkA.localeCompare(linkB);
        }
        return nameA.localeCompare(nameB);
      }
      if (sortKey === 'name') {
        return sortDir === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }

      // Soccer market-type bucket order (always ascending: moneyline → … →
      // other, unknown/non-soccer last). Tie-break A→Z by name. Keeps the
      // Tier-1 market-data-enabled precedence above, like every other sort.
      if (sortKey === 'marketType') {
        const ra = soccerMarketRank(marketTypeByKey.get(ka));
        const rb = soccerMarketRank(marketTypeByKey.get(kb));
        if (ra !== rb) return ra - rb;
        return nameA.localeCompare(nameB);
      }

      // Live metric sorts. Null metrics sink within the tier; ties fall
      // back to A→Z for a stable order instead of jitter on each frame.
      const mA = metricForSort(computePathMetrics(path, la), sortKey);
      const mB = metricForSort(computePathMetrics(path, lb), sortKey);
      if (mA == null && mB == null) return nameA.localeCompare(nameB);
      if (mA == null) return 1;
      if (mB == null) return -1;
      if (mA !== mB) return sortDir === 'desc' ? mB - mA : mA - mB;
      return nameA.localeCompare(nameB);
    };
  }, [
    store,
    sortKey,
    sortDir,
    path,
    linkByKey,
    unhedgeByKey,
    marketTypeByKey,
    venueStatusByScope,
    scopeByKey
  ]);

  // Feed the comparator + freeze flag to the store; it throttles the
  // recompute and holds the order while frozen (cells keep ticking).
  useEffect(() => {
    store.setOrderInputs({ comparator, freeze: frozen });
  }, [store, comparator, frozen]);

  // Subscribe to the store's throttled order: `useLiveOrder` returns a new
  // array ref only when the order actually changes, so this re-renders the
  // tab (and re-runs the membership sort below) at the throttled cadence, not
  // on every WS tick. The array itself is unused — `store.rankOf` is the read.
  const orderTick = useLiveOrder(store);

  // Rows are driven by the manual-confirmed `pairs` list (react-query —
  // membership is authoritative there), ordered by the store's rank.
  // `store.rankOf` resolves EITHER the pairId or the ruleId to the same rank
  // (the store dual-keys both), and returns +Infinity for pairs not yet in
  // the store (pre-WS / pre-seed) so they tail-sort by name without vanishing.
  const displayedPairs = useMemo(() => {
    const rankOf = (pair: ManualConfirmedPair): number => {
      const rule = pair._id ? rulesByPairId.get(pair._id) : undefined;
      return store.rankOf(pair._id ?? rule?._id ?? '');
    };
    return [...pairs].sort((a, b) => {
      const ra = rankOf(a);
      const rb = rankOf(b);
      if (ra !== rb) return ra - rb;
      // Both unranked (not in store) — keep a deterministic A→Z tail.
      return (a.pairName ?? '').localeCompare(b.pairName ?? '');
    });
    // `orderTick` is the throttle trigger; `store.rankOf` is read live inside
    // the sort, gated by the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairs, rulesByPairId, store, orderTick]);

  /* ---------------------------------------------------------------- */
  /* Bulk market-data ON/OFF — multi-select + master toggle           */
  /* ---------------------------------------------------------------- */

  // Checked pair ids. Selection is per-tab state: it resets on tab
  // switch, which is fine — the locked-pair set is per-tab too, so a
  // selection never carries a pair the active tab can't manage.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkOpener = useBulkSetPairOpenerEnabled();

  /* ---------------------------------------------------------------- */
  /* Assign the MAIN multiselect to a subgroup (group filter only)     */
  /* ---------------------------------------------------------------- */
  // Subgroups of the active group — only meaningful when a concrete group is
  // selected. `useSubgroups` is disabled for null ids and react-query dedups
  // with EntryPage's own call. The "Assign to subgroup…" control is hidden
  // unless the group actually HAS at least one subgroup.
  const activeGroupId =
    groupSelection.kind === 'group' ? groupSelection.groupId : null;
  const { data: subgroupsData } = useSubgroups(activeGroupId);
  const subgroups = subgroupsData?.subgroups ?? [];
  const bulkAssignSubgroup = useBulkAssignPairSubgroup();

  const handleAssignSelectedToSubgroup = useCallback(
    (pickedValue: string) => {
      const payload = buildBulkAssignSubgroupPayload(
        [...selected],
        groupSelection,
        pickedValue
      );
      if (!payload) return;
      setBulkBusy(true);
      bulkAssignSubgroup.mutate(
        {
          pairIds: payload.pairIds,
          groupId: payload.groupId,
          subgroupId: payload.subgroupId
        },
        {
          onSuccess: () => setSelected(new Set()),
          onSettled: () => setBulkBusy(false)
        }
      );
    },
    [selected, groupSelection, bulkAssignSubgroup]
  );

  // Switching the group filter drops the selection — a checked row from the
  // previous group is no longer on screen, and "Enable/Disable selected"
  // must never silently flip rows outside the now-visible group. Mirrors
  // BucketPage's view/filter-scoped selection reset. Keyed by the
  // selection's stable identity so object-reference churn (e.g. the groups
  // poll) can never wipe a selection the operator still sees.
  const groupKey = groupSelectionKey(groupSelection);
  useEffect(() => {
    setSelected(new Set());
  }, [groupKey]);

  /* ---------------------------------------------------------------- */
  /* Bulk parameter change — threshold / size / max-vol across ALL    */
  /* rules in this tab (one server request), + staged seed for the    */
  /* rule-less rows so a later arm picks up the same params.          */
  /* ---------------------------------------------------------------- */
  const bulkParams = useBulkUpdateEntryRuleParams();
  // Staged pre-rule input seed (raw strings) for rows that have NO rule
  // yet. `bulkApplyNonce` bumps on each Apply so the rows' seed effect
  // re-fires even when the same values are applied twice.
  const [bulkSeed, setBulkSeed] = useState<{
    threshold?: string;
    size?: string;
    maxVol?: string;
  }>({});
  const [bulkApplyNonce, setBulkApplyNonce] = useState(0);
  const handleBulkApplied = useCallback(
    (seed: { threshold?: string; size?: string; maxVol?: string }) => {
      setBulkSeed(seed);
      setBulkApplyNonce(n => n + 1);
    },
    []
  );

  // Rows whose checkbox is actually actionable: have an id.
  const selectableIds = useMemo(
    () => displayedPairs.map(p => p._id).filter((id): id is string => !!id),
    [displayedPairs]
  );

  // ─── Row virtualization (2026-05-23) ──────────────────────────────────
  // Mount only the visible `EntryRowByPath` instances + a top/bottom
  // spacer. Without this, hundreds of MUI `<TableRow>`s (each ~22
  // `<TableCell>` plus per-cell `sx={...}` + `<TextField>` / `<Switch>`
  // / `<Tooltip>`) were the dominant retained-memory cost — DOM nodes
  // grew linearly with the manual-confirmed-pair count and Emotion's
  // style cache piled up the per-row `sx` objects forever.
  //
  // Real row heights vary (1- vs 2-line market titles), so a fixed/estimated
  // size under-counted total height and the last rows fell outside the
  // scrollable range ("can't reach the bottom"). Each row reports its real
  // height via `measureElement`, so `getTotalSize()` is exact. `estimateSize`
  // stays as the pre-measurement seed only.
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: displayedPairs.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 52,
    overscan: 8,
    getItemKey: index => {
      const pair = displayedPairs[index];
      return pair?._id ?? pair?.pairName ?? index;
    }
  });
  const allSelected =
    selectableIds.length > 0 && selected.size >= selectableIds.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleSelect = useCallback((id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelected(prev =>
      prev.size >= selectableIds.length && selectableIds.length > 0
        ? new Set()
        : new Set(selectableIds)
    );
  }, [selectableIds]);

  // Keep the bulk action busy until the live switches actually flip. The
  // PATCH only writes `openerEnabled` in Mongo; the row switch reads
  // `pairEnabled` off the WS envelope (see `pairOpenerOn`), which lands a beat
  // later. Without this the spinner releases at PATCH-return and the operator
  // sees it flash while the switches sit on the old value. `undefined` (no
  // live envelope for that row yet) never blocks the settle.
  const openerSettled = useCallback(
    (enabled: boolean) => (s: EnrichedEntryStatus | undefined) =>
      s === undefined || (s.pairEnabled === true) === enabled,
    []
  );
  const settleOpener = useCallback(
    (ids: string[], enabled: boolean) =>
      waitForLiveSettle(store, ids, openerSettled(enabled)),
    [store, openerSettled]
  );

  // Mirror of the per-row toggle's not-confirmed warning: the settle wait is
  // a UX cap, so when it releases re-check the live snapshots and surface any
  // targeted pair the WS echo never confirmed instead of releasing silently.
  const warnUnconfirmedOpener = useCallback(
    (ids: string[], enabled: boolean, counts: BulkWriteCounts) => {
      const unconfirmed = collectUnconfirmedIds(
        store,
        ids,
        openerSettled(enabled)
      );
      if (unconfirmed.length === 0) return;
      toast(unconfirmedBulkMessage(unconfirmed.length, ids.length, counts), {
        theme: 'dark',
        type: 'warning',
        autoClose: 8000
      });
    },
    [store, openerSettled]
  );

  // scope:'ids' — flip the checked subset. Backend caps pairIds at 100 per
  // request, so chunk larger selections; chunks go out a few at a time so a
  // big selection doesn't serialize into a long tail. On a partial failure
  // the surviving chunks still flipped pairs server-side (each failed chunk
  // already fired the global mutation-error toast), so the settle +
  // unconfirmed re-check still runs over the landed counters.
  const applyBulkSelected = useCallback(
    async (enabled: boolean) => {
      const ids = [...selected];
      if (ids.length === 0) return;
      setBulkBusy(true);
      try {
        const results = await runWithConcurrency(
          chunkIds(ids, BULK_ENABLE_PAIR_IDS_CHUNK),
          BULK_ENABLE_CONCURRENCY,
          pairIds => bulkOpener.mutateAsync({ scope: 'ids', pairIds, enabled })
        );
        setSelected(new Set());
        await settleOpener(ids, enabled);
        warnUnconfirmedOpener(ids, enabled, aggregateBulkCounts(results));
      } catch (err) {
        if (!(err instanceof BulkPartialFailureError)) throw err;
        await settleOpener(ids, enabled);
        warnUnconfirmedOpener(
          ids,
          enabled,
          aggregateBulkCounts(err.partialResults as BulkWriteCounts[])
        );
      } finally {
        setBulkBusy(false);
      }
    },
    [selected, bulkOpener, settleOpener, warnUnconfirmedOpener]
  );

  // Pair ids behind the PRE-SEARCH group-scoped rows — what a server-side
  // "All ON/OFF" (`scope:'all'` [+ groupId]) actually flips, so the settle
  // + unconfirmed re-check must cover these, not just the search-narrowed
  // visible rows. Twin of `scopedRuleIds` below.
  const groupedPairIds = useMemo(
    () => groupedPairs.map(p => p._id).filter((id): id is string => !!id),
    [groupedPairs]
  );

  // "All ON / All OFF" — group-aware. No filter keeps the single
  // scope:'all' request (no id list to ship); a selected group narrows it
  // server-side via `groupId`; "Ungrouped" has no server-side scope, so it
  // falls back to scope:'ids' over the visible (already-ungrouped) pairs,
  // chunked at the backend's 100-id cap like `applyBulkSelected`. Settle +
  // re-check run over the ids the write actually targets (`bulkAllTargetIds`
  // — the pre-search group-scoped set on the scope:'all' paths), and still
  // run on a partial failure over the landed counters, mirroring
  // `applyBulkSelected`.
  const applyBulkAll = useCallback(
    async (enabled: boolean) => {
      const targetIds = bulkAllTargetIds(
        groupSelection,
        groupedPairIds,
        selectableIds
      );
      setBulkBusy(true);
      try {
        const payloads = buildBulkEnableAllPayloads(
          groupSelection,
          enabled,
          selectableIds
        );
        const results = await runWithConcurrency(
          payloads,
          BULK_ENABLE_CONCURRENCY,
          payload => bulkOpener.mutateAsync(payload)
        );
        setSelected(new Set());
        await settleOpener(targetIds, enabled);
        warnUnconfirmedOpener(targetIds, enabled, aggregateBulkCounts(results));
      } catch (err) {
        if (!(err instanceof BulkPartialFailureError)) throw err;
        await settleOpener(targetIds, enabled);
        warnUnconfirmedOpener(
          targetIds,
          enabled,
          aggregateBulkCounts(err.partialResults as BulkWriteCounts[])
        );
      } finally {
        setBulkBusy(false);
      }
    },
    [
      bulkOpener,
      groupSelection,
      groupedPairIds,
      selectableIds,
      settleOpener,
      warnUnconfirmedOpener
    ]
  );

  // Rule ids behind the PRE-SEARCH group-scoped pair set — feeds the
  // scope:'ids' variant of the bulk-params action when a group filter is
  // active. Sourced from `groupedPairs` (NOT the search-narrowed visible
  // rows) so "Apply to {group}" covers the whole group, consistent with
  // the group "All ON/OFF" which flips the whole group server-side.
  // Rule-less rows simply have nothing to update (the staged seed still
  // covers them on a later arm).
  const scopedRuleIds = useMemo(
    () =>
      groupedPairs
        .map(p => (p._id ? rulesByPairId.get(p._id)?._id : undefined))
        .filter((id): id is string => !!id),
    [groupedPairs, rulesByPairId]
  );

  // Rule ids behind the CHECKED rows — feeds the selection mode of the PM/KM
  // bulk maker-flag controls (scope:'ids'), mirroring `applyBulkSelected`
  // for market data. Rule-less checked rows simply contribute nothing.
  const selectedRuleIds = useMemo(
    () =>
      [...selected]
        .map(pairId => rulesByPairId.get(pairId)?._id)
        .filter((id): id is string => !!id),
    [selected, rulesByPairId]
  );

  if (pairs.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
          No manually confirmed pairs found.
        </Typography>
      </Box>
    );
  }

  const sortLabelSx = {
    '&.MuiTableSortLabel-root': { color: 'inherit' },
    '&.Mui-active': { color: '#42a5f5' },
    '& .MuiTableSortLabel-icon': { fontSize: '0.8rem' },
    // Show the direction arrow on EVERY sortable label, even when
    // inactive — dimmed at 40% so the active sort still pops. Lets
    // the operator see all sortable handles at a glance instead of
    // only the currently-active one.
    '&:not(.Mui-active) .MuiTableSortLabel-icon': {
      opacity: 0.4
    }
  } as const;

  // Per-column $ / % badge. Active target gets a light blue background +
  // matching text so the operator can tell which of the two is the sort
  // key at a glance — replaces the per-label arrows the column used to
  // render.
  const sortBadgeSx = (active: boolean) =>
    ({
      cursor: 'pointer',
      px: 0.4,
      borderRadius: 0.5,
      color: active ? '#42a5f5' : 'inherit',
      backgroundColor: active ? 'rgba(66, 165, 245, 0.18)' : 'transparent',
      '&:hover': { color: '#42a5f5' }
    }) as const;

  const polySideLabel = path === 'a' ? 'YES' : 'NO';
  const kalshiSideLabel = path === 'a' ? 'NO' : 'YES';

  return (
    <Box
      sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      {/* Combined control bar — market data on/off + param controls in one row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1,
          py: 0.4,
          borderBottom: '1px solid rgba(255,255,255,0.08)'
        }}
      >
        {/* LEFT — market data on/off + the param "Set all" configs. */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ flexWrap: 'wrap', minWidth: 0 }}
        >
          <BulkActionBar
            totalCount={bulkAllConfirmCount(
              groupSelection,
              groupedPairs.length,
              selectableIds.length
            )}
            groupLabel={groupLabel}
            selectedCount={selected.size}
            busy={bulkBusy}
            onEnableSelected={() => void applyBulkSelected(true)}
            onDisableSelected={() => void applyBulkSelected(false)}
            onClearSelection={() => setSelected(new Set())}
            onEnableAll={() => void applyBulkAll(true)}
            onDisableAll={() => void applyBulkAll(false)}
          />
          {/* Assign the checked rows to a subgroup — only when a concrete
              group is selected AND it has at least one subgroup. Picking an
              option fires the bulk assign + clears the selection. */}
          {selected.size > 0 &&
            groupSelection.kind === 'group' &&
            subgroups.length > 0 && (
              <Select
                size="small"
                displayEmpty
                value=""
                disabled={bulkBusy || bulkAssignSubgroup.isLoading}
                onChange={e =>
                  handleAssignSelectedToSubgroup(e.target.value as string)
                }
                renderValue={() => 'Assign to subgroup…'}
                inputProps={{ 'aria-label': 'Assign selected pairs to subgroup' }}
                sx={{
                  height: 24,
                  fontSize: '0.62rem',
                  '& .MuiSelect-select': { py: 0.2, px: 0.8 }
                }}
              >
                {subgroups.map(sg => (
                  <MenuItem
                    key={sg._id}
                    value={sg._id}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {sg.name}
                  </MenuItem>
                ))}
                <MenuItem
                  value={UNASSIGN_SUBGROUP_VALUE}
                  sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}
                >
                  Unassigned
                </MenuItem>
              </Select>
            )}
          <Box
            sx={{
              width: '1px',
              alignSelf: 'stretch',
              backgroundColor: 'rgba(255,255,255,0.12)',
              mx: 0.4
            }}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap'
            }}
          >
            <BulkParamBar
              path={path}
              selection={
                subgroupId ? { kind: 'subgroup', subgroupId } : groupSelection
              }
              scopeLabel={subgroupId ? subgroupLabel : groupLabel}
              scopedRuleIds={scopedRuleIds}
              busy={bulkParams.isLoading}
              onApply={dto => bulkParams.mutateAsync(dto)}
              onApplied={handleBulkApplied}
            />
          </Box>
        </Stack>
        {/* RIGHT — PM/KM maker on/off, sitting above the per-row CTRL column.
            Selection-aware: a live checkbox selection scopes the flip to the
            checked rows (scope:'ids'), otherwise it follows the group filter. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexShrink: 0 }}>
          <GroupArmBulkControls
            scope={
              subgroupId
                ? {
                    kind: 'subgroup',
                    subgroupId,
                    label: subgroupLabel ?? 'this subgroup'
                  }
                : {
                    kind: 'group',
                    selection: groupSelection,
                    label: groupLabel,
                    scopedRuleIds
                  }
            }
            confirmCount={bulkAllConfirmCount(
              groupSelection,
              groupedPairs.length,
              selectableIds.length
            )}
            selectedRuleIds={selectedRuleIds}
            selectedCount={selected.size}
          />
          <Box
            sx={{
              width: '1px',
              alignSelf: 'stretch',
              backgroundColor: 'rgba(255,255,255,0.12)',
              mx: 0.4
            }}
          />
          <GroupMakerBulkControls
            path={path}
            scope={{
              kind: 'group',
              selection: groupSelection,
              label: groupLabel,
              scopedRuleIds
            }}
            confirmCount={bulkAllConfirmCount(
              groupSelection,
              groupedPairs.length,
              selectableIds.length
            )}
            selectedRuleIds={selectedRuleIds}
            selectedCount={selected.size}
          />
        </Box>
      </Box>
      <TableContainer
        ref={tableContainerRef}
        sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
      >
        <Table
          size="small"
          stickyHeader
          // Stretch to fill the container; the colgroup percentages keep
          // every column's proportional share so wide screens use the
          // whole width instead of leaving the right side empty. The
          // TableContainer's `overflow: auto` scrolls on narrow ones.
          sx={{ tableLayout: 'fixed', width: '100%' }}
        >
          <colgroup>
            {COL_WIDTHS_PX.map((w, i) => (
              <col key={i} style={{ width: `${(w / COL_TOTAL_PX) * 100}%` }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...gSx,
                  width: 18,
                  px: 0,
                  verticalAlign: 'middle',
                  top: 0,
                  backgroundColor: '#1a1a2e'
                }}
                rowSpan={2}
                align="center"
                padding="none"
              >
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleSelectAll}
                  disabled={selectableIds.length === 0 || bulkBusy}
                  sx={{
                    p: 0,
                    color: 'grey.500',
                    '& .MuiSvgIcon-root': { fontSize: 14 }
                  }}
                />
              </TableCell>
              <TableCell
                sx={{ ...gSx, width: 480, top: 0, backgroundColor: '#1a1a2e' }}
                rowSpan={2}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  spacing={0.5}
                >
                  <TableSortLabel
                    active={sortKey === 'name'}
                    direction={sortKey === 'name' ? sortDir : 'asc'}
                    onClick={() => handleSortClick('name')}
                    sx={sortLabelSx}
                  >
                    Market
                  </TableSortLabel>
                  <TableSortLabel
                    active={sortKey === 'marketType'}
                    direction={sortKey === 'marketType' ? sortDir : 'asc'}
                    onClick={() => handleSortClick('marketType')}
                    sx={sortLabelSx}
                  >
                    Type
                  </TableSortLabel>
                  <IconButton
                    size="small"
                    onClick={() => setFrozen(f => !f)}
                    aria-label={frozen ? 'resume live sorting' : 'freeze sort'}
                    sx={{ p: 0.1, color: frozen ? '#42a5f5' : 'grey.600' }}
                  >
                    <AcUnitIcon sx={{ fontSize: '0.85rem' }} />
                  </IconButton>
                </Stack>
              </TableCell>
              <TableCell
                sx={{ ...gSx, width: 56, top: 0, backgroundColor: '#1a1a2e' }}
                rowSpan={2}
                align="center"
              >
                YES
              </TableCell>
              <TableCell
                sx={{ ...gSx, width: 56, top: 0, backgroundColor: '#1a1a2e' }}
                rowSpan={2}
                align="center"
              >
                NO
              </TableCell>
              <TableCell
                sx={{ ...gSx, width: 40, top: 0, backgroundColor: '#1a1a2e', cursor: 'pointer' }}
                rowSpan={2}
                align="center"
                onClick={() => handleSortClick('unhedge')}
              >
                <TableSortLabel
                  active={sortKey === 'unhedge'}
                  direction={sortKey === 'unhedge' ? sortDir : 'desc'}
                  sx={sortLabelSx}
                >
                  ACT
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
                colSpan={4}
              >
                Polymarket {polySideLabel}
              </TableCell>
              <TableCell
                sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
                colSpan={4}
              >
                Kalshi {kalshiSideLabel}
              </TableCell>
              <TableCell
                sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
                colSpan={11}
              >
                Parameters
              </TableCell>
              {/* Per-strategy groups — each spans 2 sub-cells: [legs] + [$/%].
                Legs show maker leg (blue) + taker leg (orange) on the two
                ACTUAL platforms the strategy uses, so the operator never has
                to mentally combine prices from different columns to read a
                strategy. Mirrors the unwinder's Sim-P-Mk / Sim-K-Mk layout
                (UnwindingTabSumOfSides). */}
              <TableCell
                sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
                colSpan={2}
                title="Taker-Taker: both legs fire as taker (orange = all-in price each leg pays)."
              >
                TT
              </TableCell>
              <TableCell
                sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
                colSpan={2}
                title="Poly maker: REST on Polymarket (blue) + TAKE on Kalshi (orange)."
              >
                P Mk
              </TableCell>
              <TableCell
                sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
                colSpan={2}
                title="Kalshi maker: REST on Kalshi (blue) + TAKE on Polymarket (orange)."
              >
                K Mk
              </TableCell>
              <TableCell
                sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
                colSpan={3}
              >
                Ctrl
              </TableCell>
            </TableRow>
            <TableRow>
              {/* Polymarket — Qty · Bid · Ask · Qty */}
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 24,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Qty
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 38,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Bid
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 38,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Ask
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 24,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Qty
              </TableCell>
              {/* Kalshi — Qty · Bid · Ask · Qty */}
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 24,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Qty
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 38,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Bid
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 38,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Ask
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 24,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Qty
              </TableCell>
              {/* Parameters */}
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 44,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Thr
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 30,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                Size
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 36,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="right"
              >
                MaxV
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 64,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="center"
              >
                Mode
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 64,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="center"
              >
                Hedge
              </TableCell>
              {/* Counter-stuck market-hedge timeout (seconds) — unfilled
                  counter maker after N s → cancel + hedge at market.
                  Empty = off (inherits the opener group's fallback). */}
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 34,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="center"
              >
                Wait
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 40,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="center"
              >
                Gate
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 40,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="center"
              >
                {/* Sweep-response (rising-leg-first) on/off. Header text stays
                    the short "Sweep" — the column is 40px fixed and the table
                    bans hover explainers (see openerHoversSimOnly.test.ts). */}
                Sweep
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 48,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="center"
              >
                Walk (s)
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 48,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="center"
              >
                Walk (¢)
              </TableCell>
              {/* Per-row batch-confirm ✓ — commits ALL staged Parameters-group
                  edits (Thr/Size/MaxV/Mode/Hedge/Wait/Gate/Sweep/Walk) for the
                  row in a single PATCH. Grey when the row is clean, highlighted
                  when it has pending draft edits. */}
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 28,
                  px: 0.1,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="center"
              >
                ✓
              </TableCell>
              {/* Sim sub-row — per-strategy pair: [Legs placeholder] + [$/% sort].
                Legs cell has no sort handle; the $/% cell keeps the existing
                two-target sort UI ($ → sum, % → tna, arrow toggles direction).
                Strategy name (TT / P Mk / K Mk) lives in the group header row
                above, spanning both sub-cells. */}
              {(
                [
                  {
                    sumKey: 'sumTT',
                    tnaKey: 'tnaTT',
                    tip: 'Taker-Taker — click $ or % to switch sort target; arrow toggles direction.'
                  },
                  {
                    sumKey: 'sumPolyMk',
                    tnaKey: 'tnaPolyMk',
                    tip: 'Poly maker — click $ or % to switch sort target; arrow toggles direction.'
                  },
                  {
                    sumKey: 'sumKalshiMk',
                    tnaKey: 'tnaKalshiMk',
                    tip: 'Kalshi maker — click $ or % to switch sort target; arrow toggles direction.'
                  }
                ] as const
              ).map(({ sumKey, tnaKey, tip }) => {
                const sumActive = sortKey === sumKey;
                const tnaActive = sortKey === tnaKey;
                const colActive = sumActive || tnaActive;
                const ArrowIcon =
                  colActive && sortDir === 'asc'
                    ? ArrowUpwardIcon
                    : ArrowDownwardIcon;
                // Arrow click: toggle direction when this column is the
                // active sort; otherwise activate the $ sort for this
                // column (matches the default-direction logic in
                // handleSortClick).
                const arrowKey = tnaActive ? tnaKey : sumKey;
                return (
                  <Fragment key={sumKey}>
                    {/* Legs placeholder — no sort. Width matches the body
                      legs cell below. */}
                    <TableCell
                      sx={{
                        ...hSx,
                        ...subHSx,
                        width: 50,
                        top: ROW1_HEIGHT,
                        backgroundColor: '#1a1a2e'
                      }}
                    />
                    <TableCell
                      sx={{
                        ...hSx,
                        ...subHSx,
                        width: 50,
                        top: ROW1_HEIGHT,
                        backgroundColor: '#1a1a2e'
                      }}
                      align="right"
                      title={tip}
                    >
                      <Stack
                        direction="row"
                        spacing={0.2}
                        justifyContent="flex-end"
                        alignItems="center"
                      >
                        <Box
                          component="span"
                          onClick={() => handleSortClick(sumKey)}
                          sx={sortBadgeSx(sumActive)}
                        >
                          $
                        </Box>
                        <ArrowIcon
                          onClick={() => handleSortClick(arrowKey)}
                          sx={{
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                            color: colActive ? '#42a5f5' : 'inherit',
                            opacity: colActive ? 1 : 0.4
                          }}
                        />
                        <Box
                          component="span"
                          onClick={() => handleSortClick(tnaKey)}
                          sx={sortBadgeSx(tnaActive)}
                        >
                          %
                        </Box>
                      </Stack>
                    </TableCell>
                  </Fragment>
                );
              })}
              {/* Ctrl — 3 switches: PolyMk · KalshiMk · TT */}
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 42,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="center"
              >
                P
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 42,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="center"
              >
                K
              </TableCell>
              <TableCell
                sx={{
                  ...hSx,
                  ...subHSx,
                  width: 42,
                  top: ROW1_HEIGHT,
                  backgroundColor: '#1a1a2e'
                }}
                align="center"
              >
                TT
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              const virtualItems = rowVirtualizer.getVirtualItems();
              const totalSize = rowVirtualizer.getTotalSize();
              const paddingTop =
                virtualItems.length > 0 ? virtualItems[0].start : 0;
              const paddingBottom =
                virtualItems.length > 0
                  ? totalSize - virtualItems[virtualItems.length - 1].end
                  : 0;
              // Spacer rows preserve the scrollbar size; `colSpan={100}` is
              // a known-safe "all columns" idiom — the browser clamps to
              // the actual count and the cell stays invisible.
              return (
                <>
                  {paddingTop > 0 && (
                    <TableRow style={{ height: paddingTop }}>
                      <TableCell
                        colSpan={100}
                        sx={{ p: 0, border: 'none' }}
                        aria-hidden
                      />
                    </TableRow>
                  )}
                  {virtualItems.map(vi => {
                    const pair = displayedPairs[vi.index];
                    const rule = pair._id
                      ? rulesByPairId.get(pair._id)
                      : undefined;
                    const anyRule = pair._id
                      ? anyRulesByPairId.get(pair._id)
                      : undefined;
                    const pairKey = pair._id ?? pair.pairName;
                    return (
                      <EntryRowByPath
                        key={pairKey}
                        dataIndex={vi.index}
                        measureRef={rowVirtualizer.measureElement}
                        path={path}
                        pair={pair}
                        rule={rule}
                        anyRule={anyRule}
                        store={store}
                        selected={pair._id ? selected.has(pair._id) : false}
                        onToggleSelect={toggleSelect}
                        contracts={
                          pair._id
                            ? (contractsByPairId.get(pair._id) ??
                              ZERO_CONTRACTS)
                            : ZERO_CONTRACTS
                        }
                        venueStatus={
                          venueStatusByScope[
                            rowAccountScope(pair.openerGroupId, groupAccounts)
                          ]
                        }
                        onCreateRule={onCreateRule}
                        onSetMakerFlag={onSetMakerFlag}
                        onUpdateRule={onUpdateRule}
                        onHidePair={onHidePair}
                        bulkSeed={bulkSeed}
                        bulkApplyNonce={bulkApplyNonce}
                      />
                    );
                  })}
                  {paddingBottom > 0 && (
                    <TableRow style={{ height: paddingBottom }}>
                      <TableCell
                        colSpan={100}
                        sx={{ p: 0, border: 'none' }}
                        aria-hidden
                      />
                    </TableRow>
                  )}
                </>
              );
            })()}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Bulk Parameter Bar                                                  */
/* ------------------------------------------------------------------ */
//
// Three compact numeric inputs (Threshold / Size / Max Vol) + a maker
// size-mode select (— / Dynamic / Fixed) + an "Apply to all" button. A
// blank field (or "—" mode) means "leave that param alone".
// On Apply: parse + validate only the filled fields, hit the bulk
// endpoint to update every EXISTING rule in scope for this path, then
// hand the raw strings up via `onApplied` so the parent stages them as
// the pre-rule seed for rule-less rows (without creating any rule).
// Scope follows the page's group filter: no filter keeps the single
// scope:'all' request; a group / Ungrouped filter resolves to the visible
// rows' rule ids (scope:'ids', chunked) via `buildBulkParamsDtos`. When a
// concrete subgroup is selected the bar narrows scope:'all' server-side via
// `subgroupId` (LIVE one-shot apply — distinct from the subgroup snapshot
// "Config & apply"), mirroring the subgroup maker-flag flip.

interface BulkParamBarProps {
  path: 'a' | 'b';
  selection: BulkParamSelection;
  /** Display name of the active scope (group OR subgroup); null = All groups.
   *  Quoted in the "Apply to {scope}" button. */
  scopeLabel: string | null;
  scopedRuleIds: string[];
  busy: boolean;
  onApply: (dto: {
    scope?: 'all' | 'ids';
    ruleIds?: string[];
    subgroupId?: string;
    path: 'a' | 'b';
    thresholdSum?: number;
    size?: number;
    maxVolumeContracts?: number;
    makerSizeMode?: 'dynamic' | 'fixed';
    disablePlacementGatePolymarket?: boolean;
    disablePlacementGateKalshi?: boolean;
  }) => Promise<{ scope: string; matched: number; modified: number }>;
  onApplied: (seed: {
    threshold?: string;
    size?: string;
    maxVol?: string;
  }) => void;
}

function BulkParamBar({
  path,
  selection,
  scopeLabel,
  scopedRuleIds,
  busy,
  onApply,
  onApplied
}: BulkParamBarProps) {
  const [threshold, setThreshold] = useState('');
  const [size, setSize] = useState('');
  const [maxVol, setMaxVol] = useState('');
  const [sizeMode, setSizeMode] = useState<'' | 'dynamic' | 'fixed'>('');
  const [bulkLockstep, setBulkLockstep] = useState<boolean | null>(null); // null = no change
  // Split per-platform "No gate" tristates. null = no change (omit the key).
  const [bulkPolyGate, setBulkPolyGate] = useState<boolean | null>(null);
  const [bulkKalshiGate, setBulkKalshiGate] = useState<boolean | null>(null);
  const [walkSecs, setWalkSecs] = useState('');
  const [walkCents, setWalkCents] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [modified, setModified] = useState<number | null>(null);

  const inSx = {
    '& .MuiInputBase-input': {
      fontSize: '0.7rem',
      py: 0.2,
      px: 0.4,
      textAlign: 'right',
      width: 44
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255,255,255,0.08)'
    },
    minWidth: 0
  } as const;

  const labelSx = {
    fontSize: '0.62rem',
    color: 'grey.500',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap' as const
  };

  const handleApply = useCallback(async () => {
    setError(null);
    const values: BulkParamsValues = {};
    const seed: { threshold?: string; size?: string; maxVol?: string } = {};

    const tRaw = threshold.trim();
    if (tRaw !== '') {
      const t = Number.parseFloat(tRaw);
      // Bulk scope can mix hedge modes (and per-rule allow-above-one flags),
      // so the FE guard uses the most permissive ceiling (the gtc/market mode
      // cap); per-rule backend validation rejects any value that's invalid for
      // a specific rule's mode/flag (e.g. > $1 on a non-opted always-threshold).
      if (!Number.isFinite(t) || t <= 0 || t > ENTRY_THRESHOLD_CAP_MODE_HEDGE) {
        setError(`Threshold must be 0 < x ≤ ${ENTRY_THRESHOLD_CAP_MODE_HEDGE.toFixed(2)}`);
        return;
      }
      values.thresholdSum = t;
      seed.threshold = tRaw;
    }

    const sRaw = size.trim();
    if (sRaw !== '') {
      const s = Number.parseInt(sRaw, 10);
      if (!Number.isInteger(s) || s < 1) {
        setError('Size must be an integer ≥ 1');
        return;
      }
      values.size = s;
      seed.size = String(s);
    }

    const vRaw = maxVol.trim();
    if (vRaw !== '') {
      const v = Number.parseFloat(vRaw);
      if (!Number.isFinite(v) || v < 1) {
        setError('Max Vol must be ≥ 1');
        return;
      }
      values.maxVolumeContracts = v;
      seed.maxVol = vRaw;
    }

    if (sizeMode !== '') {
      values.makerSizeMode = sizeMode;
    }

    if (bulkLockstep === true) {
      values.lockstepMaxAheadBatches = 0;
    }

    if (bulkPolyGate !== null) {
      values.disablePlacementGatePolymarket = bulkPolyGate;
    }
    if (bulkKalshiGate !== null) {
      values.disablePlacementGateKalshi = bulkKalshiGate;
    }

    const wsRaw = walkSecs.trim();
    if (wsRaw !== '') {
      const ws = Number.parseInt(wsRaw, 10);
      if (!Number.isInteger(ws) || ws < 1) {
        setError('Walk (s) must be an integer ≥ 1');
        return;
      }
      values.counterMakerWalkStepSeconds = ws;
    }

    const wcRaw = walkCents.trim();
    if (wcRaw !== '') {
      const wc = Number.parseInt(wcRaw, 10);
      if (!Number.isInteger(wc) || wc < 1) {
        setError('Walk (¢) must be an integer ≥ 1');
        return;
      }
      values.counterMakerWalkStepCents = wc;
    }

    if (
      values.thresholdSum === undefined &&
      values.size === undefined &&
      values.maxVolumeContracts === undefined &&
      values.makerSizeMode === undefined &&
      values.lockstepMaxAheadBatches === undefined &&
      values.disablePlacementGatePolymarket === undefined &&
      values.disablePlacementGateKalshi === undefined &&
      values.counterMakerWalkStepSeconds === undefined &&
      values.counterMakerWalkStepCents === undefined
    ) {
      setError('Fill at least one field');
      return;
    }

    try {
      // One request for the unfiltered scope:'all' path; chunked
      // scope:'ids' requests when a group filter is active. An empty dto
      // list (filtered view with no rules yet) updates nothing but still
      // stages the seed for the rule-less rows.
      const dtos = buildBulkParamsDtos(selection, path, values, scopedRuleIds);
      setModified(await applyBulkParamsDtos(dtos, onApply));
      setSizeMode('');
      setBulkLockstep(null);
      setBulkPolyGate(null);
      setBulkKalshiGate(null);
      setWalkSecs('');
      setWalkCents('');
      onApplied(seed);
    } catch {
      setError('Bulk update failed');
    }
  }, [path, selection, scopedRuleIds, threshold, size, maxVol, sizeMode, bulkLockstep, bulkPolyGate, bulkKalshiGate, walkSecs, walkCents, onApply, onApplied]);

  return (
    <>
      <Typography sx={labelSx}>Set all</Typography>
      <Typography sx={labelSx}>Thr</Typography>
      <TextField
        size="small"
        value={threshold}
        onChange={e => setThreshold(e.target.value)}
        placeholder="—"
        disabled={busy}
        sx={inSx}
        inputProps={{ 'aria-label': 'bulk threshold', inputMode: 'decimal' }}
      />
      <Typography sx={labelSx}>Size</Typography>
      <TextField
        size="small"
        value={size}
        onChange={e => setSize(e.target.value)}
        placeholder="—"
        disabled={busy}
        sx={inSx}
        inputProps={{ 'aria-label': 'bulk size', inputMode: 'numeric' }}
      />
      <Typography sx={labelSx}>Max Vol</Typography>
      <TextField
        size="small"
        value={maxVol}
        onChange={e => setMaxVol(e.target.value)}
        placeholder="—"
        disabled={busy}
        sx={inSx}
        inputProps={{ 'aria-label': 'bulk max vol', inputMode: 'numeric' }}
      />
      <Typography sx={labelSx}>Mode</Typography>
      <Select
        size="small"
        value={sizeMode}
        displayEmpty
        onChange={e => setSizeMode(e.target.value as '' | 'dynamic' | 'fixed')}
        disabled={busy}
        inputProps={{ 'aria-label': 'bulk maker size mode' }}
        sx={{
          fontSize: '0.7rem',
          '& .MuiSelect-select': { py: 0.2, pl: 0.5, pr: '20px !important' },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.08)'
          }
        }}
      >
        <MenuItem value="" sx={{ fontSize: '0.7rem' }}>
          —
        </MenuItem>
        <MenuItem value="dynamic" sx={{ fontSize: '0.7rem' }}>
          Dynamic
        </MenuItem>
        <MenuItem value="fixed" sx={{ fontSize: '0.7rem' }}>
          Fixed
        </MenuItem>
      </Select>
      <HedgeModeMaxSumControls />
      <Typography sx={labelSx}>Wait ✓</Typography>
      <Checkbox
        size="small"
        checked={bulkLockstep === true}
        indeterminate={bulkLockstep === null}
        disabled={busy}
        onChange={e => setBulkLockstep(e.target.checked ? true : null)}
        sx={{ p: 0.3, '& .MuiSvgIcon-root': { fontSize: 16 } }}
      />
      <Typography sx={labelSx}>No gate P</Typography>
      <Checkbox
        size="small"
        checked={bulkPolyGate === true}
        indeterminate={bulkPolyGate === null}
        disabled={busy}
        onChange={e => setBulkPolyGate(e.target.checked ? true : null)}
        inputProps={{
          'aria-label':
            'Disable placement gate — Polymarket (keep posting far from threshold — illiquid)'
        }}
        sx={{ p: 0.3, '& .MuiSvgIcon-root': { fontSize: 16 } }}
      />
      <Typography sx={labelSx}>No gate K</Typography>
      <Checkbox
        size="small"
        checked={bulkKalshiGate === true}
        indeterminate={bulkKalshiGate === null}
        disabled={busy}
        onChange={e => setBulkKalshiGate(e.target.checked ? true : null)}
        inputProps={{
          'aria-label':
            'Disable placement gate — Kalshi (keep posting far from threshold — illiquid)'
        }}
        sx={{ p: 0.3, '& .MuiSvgIcon-root': { fontSize: 16 } }}
      />
      <GlobalGateToggles />
      <SweepConfigButton />
      <Typography sx={labelSx}>Walk (s)</Typography>
      <TextField
        size="small"
        value={walkSecs}
        onChange={e => setWalkSecs(e.target.value)}
        placeholder="—"
        disabled={busy}
        sx={inSx}
        inputProps={{ 'aria-label': 'bulk walk seconds', inputMode: 'numeric' }}
      />
      <Typography sx={labelSx}>Walk (¢)</Typography>
      <TextField
        size="small"
        value={walkCents}
        onChange={e => setWalkCents(e.target.value)}
        placeholder="—"
        disabled={busy}
        sx={inSx}
        inputProps={{ 'aria-label': 'bulk walk cents', inputMode: 'numeric' }}
      />
      <Button
        size="small"
        variant="outlined"
        disabled={busy}
        onClick={() => void handleApply()}
        sx={{ fontSize: '0.62rem', py: 0.1, px: 0.8, minWidth: 0 }}
      >
        {selection.kind === 'all'
          ? 'Apply to all'
          : selection.kind === 'subgroup'
            ? `Apply to ${scopeLabel ?? 'subgroup'}`
            : `Apply to ${scopeLabel ?? 'group'}`}
      </Button>
      {busy && <CircularProgress size={12} />}
      {error && (
        <Typography sx={{ fontSize: '0.62rem', color: '#f44336' }}>
          {error}
        </Typography>
      )}
      {!error && modified != null && (
        <Typography sx={{ fontSize: '0.62rem', color: '#4caf50' }}>
          Updated {modified} rule{modified === 1 ? '' : 's'}
        </Typography>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Row                                                                */
/* ------------------------------------------------------------------ */

interface RowProps {
  /** Virtual-row index — drives `data-index` for react-virtual's measurer. */
  dataIndex: number;
  /** react-virtual's `measureElement` ref callback; lands on the main row. */
  measureRef: (el: HTMLTableRowElement | null) => void;
  path: 'a' | 'b';
  pair: ManualConfirmedPair;
  rule?: EntryRule;
  anyRule?: EntryRule;
  /** Per-pair external store. The row subscribes to its OWN key
   *  (`pair._id ?? rule._id`) so only it re-renders on its WS frame. */
  store: LiveStore<EnrichedEntryStatus>;
  /** Whether this row's bulk-select checkbox is ticked. */
  selected: boolean;
  /** Toggle this pair in the bulk-select set. */
  onToggleSelect: (id: string) => void;
  /** REST (main-account) contracts for this pair — usable ONLY on a view
   *  proven to be the main account (`venueStatus.restAllowed`). */
  contracts: PairContracts;
  /** Which source this row's ACT / YES / NO cells may render. */
  venueStatus: VenueViewStatus;
  onCreateRule: CreateEntryRuleFn;
  onSetMakerFlag: (
    rule: EntryRule,
    flag: MakerFlag,
    value: boolean,
    clearOthers?: MakerFlag[]
  ) => Promise<void> | void;
  onUpdateRule: (rule: EntryRule, dto: UpdateEntryRuleDto) => Promise<boolean>;
  onHidePair: (pair: ManualConfirmedPair) => Promise<void> | void;
  /** Staged bulk-param seed (raw strings) for RULE-LESS rows — an absent
   *  field keeps the hard default. Existing rules ignore this (the backend
   *  bulk endpoint + refetch-sync drive them). */
  bulkSeed: { threshold?: string; size?: string; maxVol?: string };
  /** Bumps on each bulk Apply so the seed effect re-fires for rule-less rows
   *  even when the same values are re-applied. */
  bulkApplyNonce: number;
}

export const EntryRowByPath = memo(
  ({
    dataIndex,
    measureRef,
    path,
    pair,
    rule,
    anyRule,
    store,
    selected,
    onToggleSelect,
    contracts,
    venueStatus,
    onCreateRule,
    onSetMakerFlag,
    onUpdateRule,
    onHidePair,
    bulkSeed,
    bulkApplyNonce
  }: RowProps) => {
    // Subscribe to THIS pair's live state — only this row re-renders when
    // its pair's WS frame lands. Look up by pairId first, falling back to
    // ruleId (mirrors the legacy `pairId ?? ruleId` resolution).
    const liveState = useLiveState(store, pair._id ?? rule?._id ?? '');

    // ONE source decision per row, shared by the YES / NO counts and the ACT
    // net (and matching the ACT sort, which resolves the same view): the three
    // cells go dark together, and none of them can substitute a main-account
    // number for a group whose own account we cannot read.
    const venueView = resolvePairVenueView(
      venueStatus,
      liveState?.venuePositions
    );
    const displayContracts = contractsViewFor(venueView, contracts);
    const contractsTitle = contractsNote(displayContracts, venueStatus);

    const ruleSize =
      rule?.kalshiConfig?.contractsToBuy ??
      rule?.polymarketConfig?.contractsToBuy ??
      DEFAULT_SIZE;
    // Each tab edits its OWN path's threshold; fall back to the rule-level
    // seed, then the default.
    const perPathThreshold =
      path === 'a' ? rule?.pathA?.thresholdSum : rule?.pathB?.thresholdSum;
    // Rule-less rows prefer the staged bulk seed over the hard default so a
    // later manual arm picks up the operator's bulk params; existing rules
    // always seed from their own server values.
    const initThreshold =
      perPathThreshold != null
        ? String(perPathThreshold)
        : rule?.thresholdSum != null
          ? String(rule.thresholdSum)
          : (bulkSeed.threshold ?? String(DEFAULT_THRESHOLD));
    const initSize = rule
      ? String(ruleSize)
      : (bulkSeed.size ?? String(DEFAULT_SIZE));
    const initMaxVol =
      rule?.maxVolumeContracts != null
        ? String(rule.maxVolumeContracts)
        : (bulkSeed.maxVol ?? String(DEFAULT_MAX_VOL));

    const [threshold, setThreshold] = useState(initThreshold);
    const [size, setSize] = useState(initSize);
    const [maxVol, setMaxVol] = useState(initMaxVol);
    const [savedThreshold, setSavedThreshold] = useState(initThreshold);
    const [savedSize, setSavedSize] = useState(initSize);
    const [savedMaxVol, setSavedMaxVol] = useState(initMaxVol);
    const [thresholdError, setThresholdError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [walkSecsInput, setWalkSecsInput] = useState(
      rule?.counterMakerWalkStepSeconds != null
        ? String(rule.counterMakerWalkStepSeconds)
        : ''
    );
    const [walkCentsInput, setWalkCentsInput] = useState(
      rule?.counterMakerWalkStepCents != null
        ? String(rule.counterMakerWalkStepCents)
        : ''
    );
    // Committed walk values — drive the per-field confirmation tic (✓) so the
    // walk params behave like Threshold/Size/MaxVol instead of saving silently.
    const [savedWalkSecs, setSavedWalkSecs] = useState(
      rule?.counterMakerWalkStepSeconds != null
        ? String(rule.counterMakerWalkStepSeconds)
        : ''
    );
    const [savedWalkCents, setSavedWalkCents] = useState(
      rule?.counterMakerWalkStepCents != null
        ? String(rule.counterMakerWalkStepCents)
        : ''
    );
    // Pre-rule pending selections. Before a rule exists the two mode
    // selectors edit these instead of hitting the API; the create
    // call-sites seed them into the new rule.
    const [pendingSizeMode, setPendingSizeMode] = useState<
      'dynamic' | 'fixed'
    >('fixed');
    const [pendingHedgeMode, setPendingHedgeMode] =
      useState<HedgeModeSelectValue>(INHERIT_SENTINEL);
    // Wait / Gate / Sweep are now per-row DRAFT staged (like Thr/Size/MaxV/
    // Mode/Hedge/Walk) instead of PATCHing on change — the per-row ✓ commits
    // them. Draft booleans seed from the rule's current value (or off when no
    // rule yet). Wait = strict lockstep (`lockstepMaxAheadBatches === 0`).
    const lockstepBaseline = rule?.lockstepMaxAheadBatches === 0;
    const polyGateBaseline = rule?.disablePlacementGatePolymarket === true;
    const kalshiGateBaseline = rule?.disablePlacementGateKalshi === true;
    const sweepBaseline = rule?.sweepDualPostEnabled === true;
    const [pendingLockstep, setPendingLockstep] = useState<boolean>(
      lockstepBaseline
    );
    const [pendingPolyGate, setPendingPolyGate] = useState<boolean>(
      polyGateBaseline
    );
    const [pendingKalshiGate, setPendingKalshiGate] = useState<boolean>(
      kalshiGateBaseline
    );
    const [pendingSweep, setPendingSweep] = useState<boolean>(sweepBaseline);
    // Per-pair "> $1" opt-in, staged like the other params so a rule-less OR
    // paused row can toggle it and carry it into the create. The pill is always
    // shown (not gated on an existing rule) — see the render below.
    const [pendingAllowAboveOne, setPendingAllowAboveOne] = useState<boolean>(
      rule?.allowThresholdAboveOne === true
    );
    // Optimistic maker-switch overlay: flag → desired value, set
    // SYNCHRONOUSLY on click so the switch flips the instant it's toggled,
    // independent of how many round-trips run behind it (auto-enable the
    // pair, create the rule, then PATCH the flag). Cleared once the mutation
    // settles — by then the rule cache holds server truth (or the rollback),
    // so the switch hands off without a flicker. Replaces the old
    // spinner-swap, which hid the switch for the whole round-trip and made
    // the toggle look frozen ("no cambia hasta después").
    const [pendingFlags, setPendingFlags] = useState<
      Partial<Record<MakerFlag, boolean>>
    >({});
    const [togglingTT, setTogglingTT] = useState(false);
    const [hidePending, setHidePending] = useState(false);

    // Per-pair opener kill switch. Default OFF for manual-confirmed pairs;
    // the operator turns it on to start streaming WS data + arming rules.
    // No confirmation dialog here — turning off has no live position to
    // worry about (the opener never holds positions; entries settle into
    // the unwinder), unlike the unwinder side.
    const pairOpenerOn = liveState?.pairEnabled === true;
    const setPairOpenerEnabled = useSetPairOpenerEnabled();
    const setRuleArmed = useSetRuleArmed();

    // D6/O7 — the on/off switch reflects the rule's REAL armed state. `checked`
    // is driven by the authoritative `ruleArmed` field the engine echoes on the
    // WS envelope (NOT the pair-level `pairEnabled` kill switch), never an
    // optimistic patch — so it can never paint ON while the rule is paused (or
    // vice-versa). Back-compat: when an older backend omits `ruleArmed`,
    // `resolveArmSwitchState` falls back to `pairEnabled`. `pendingOpener` holds
    // the operator's last intent purely to show an in-progress label + lock the
    // control until the WS echoes the applied state (or a UX-cap timeout /
    // failure clears it).
    const [pendingOpener, setPendingOpener] = useState<boolean | undefined>(
      undefined
    );
    const armSwitch = resolveArmSwitchState({
      ruleArmed: liveState?.ruleArmed,
      pairEnabled: pairOpenerOn,
      pending: pendingOpener,
      disabled: setPairOpenerEnabled.isLoading || !pair._id
    });
    const handleOpenerToggle = useCallback(
      async (next: boolean) => {
        if (!pair._id) return;
        setPendingOpener(next);
        try {
          // Pair-level kill switch (WS streaming + the backend's rule
          // auto-pause cascade on `false`).
          await setPairOpenerEnabled.mutateAsync({
            pairId: pair._id,
            enabled: next
          });
          // Explicit, durable arm/disarm intent routed to the OWNING feed pod
          // (spec D5) when a rule exists. `desiredArmed` is the single boolean
          // that arms/pauses — turning OFF disarms durably so a stray/other
          // -writer `update` can't silently re-arm the rule.
          if (rule) {
            await setRuleArmed.mutateAsync({
              id: rule._id,
              desiredArmed: next
            });
          }
          // Hold the in-progress label until the WS envelope echoes the new
          // real armed state (or the UX cap). `checked` already shows the
          // authoritative value, so this only governs the label + lock. We key
          // the echo off the SAME authoritative resolution as `checked` —
          // `ruleArmed` with a `pairEnabled` back-compat fallback — so the
          // label clears exactly when the switch settles on the real state.
          await waitForLiveSettle(
            store,
            [pair._id],
            s =>
              resolveWsArmed({
                ruleArmed: s?.ruleArmed,
                pairEnabled: s?.pairEnabled === true
              }) === next,
            { timeoutMs: 8000 }
          );
          const settled = store.getKeySnapshot(pair._id);
          const confirmed =
            resolveWsArmed({
              ruleArmed: settled?.ruleArmed,
              pairEnabled: settled?.pairEnabled === true
            }) === next;
          if (!confirmed) {
            // Write succeeded but the backend never echoed the new state
            // within the cap — surface it; the switch reverts to the real
            // (possibly still-armed) state, never a silent OFF.
            toast(
              next
                ? 'Enable not confirmed — the backend has not echoed ON yet. Verify before relying on it.'
                : 'Disable not confirmed — the rule may still be ACTIVE. Verify it actually stopped.',
              { theme: 'dark', type: 'warning', autoClose: 8000 }
            );
          }
        } catch {
          // Mutation rejections already surface via the global
          // MutationCache.onError toast; just drop the pending overlay so the
          // switch reverts to the authoritative backend state.
        } finally {
          setPendingOpener(undefined);
        }
      },
      [pair._id, rule, setPairOpenerEnabled, setRuleArmed, store]
    );

    // Live global hedge config (shared react-query cache — one fetch for the
    // whole table). Resolves the per-rule hedge Select's option labels so
    // "Inherit" always names the concrete mode + target it maps to.
    const { data: systemConfig } = useSystemConfig();
    const hedgeOptions = hedgeModeOptions({
      mode: systemConfig?.counterHedgeModeDefault,
      maxSum: systemConfig?.marketHedgeMaxSum
    });
    const setAllowThresholdAboveOne = useSetAllowThresholdAboveOne();

    // Effective threshold ceiling for THIS row — hedge-mode + flag aware.
    // The mode is read from the STAGED hedge selection (`pendingHedgeMode`,
    // which seeds from `rule.counterHedgeMode`) so the bound tracks what the
    // row is editing toward; inherit (null) resolves to the global default.
    // `allowThresholdAboveOne` is a UNIVERSAL gate for crossing $1 (every mode);
    // the per-mode ceiling only kicks in once it's enabled.
    // Display value: an existing rule's persisted flag, else the staged draft
    // (rule-less rows edit the draft and carry it into the create).
    const allowAboveOne = rule
      ? rule.allowThresholdAboveOne === true
      : pendingAllowAboveOne;
    const effectiveHedgeMode = resolveEffectiveHedgeMode(
      fromSelectValue(pendingHedgeMode),
      systemConfig?.counterHedgeModeDefault
    );
    const tBound = useMemo(
      () => thresholdBound(effectiveHedgeMode, allowAboveOne),
      [effectiveHedgeMode, allowAboveOne]
    );
    // Keep the staged flag in step with the rule's persisted value once the
    // rule loads / is created. For a rule-less row `rule?.allowThresholdAboveOne`
    // is a stable `undefined`, so a toggled draft is never clobbered by a poll.
    useEffect(() => {
      setPendingAllowAboveOne(rule?.allowThresholdAboveOne === true);
    }, [rule?.allowThresholdAboveOne]);
    const allowAboveOneTooltip = allowAboveOne
      ? 'Threshold > $1 enabled — this pair may rest above $1. Click to disable.'
      : 'Enable threshold > $1 for this pair (required to cross $1 in any hedge mode).';

    useEffect(() => {
      if (!rule) return;
      const dirty =
        threshold !== savedThreshold ||
        size !== savedSize ||
        maxVol !== savedMaxVol;
      if (dirty) return;
      const perPath =
        path === 'a' ? rule.pathA?.thresholdSum : rule.pathB?.thresholdSum;
      const nextThreshold = perPath != null ? perPath : rule.thresholdSum;
      if (nextThreshold != null) {
        const next = String(nextThreshold);
        if (next !== savedThreshold) {
          setThreshold(next);
          setSavedThreshold(next);
        }
      }
      const ruleSizeNow =
        rule.kalshiConfig?.contractsToBuy ??
        rule.polymarketConfig?.contractsToBuy;
      if (ruleSizeNow != null) {
        const next = String(ruleSizeNow);
        if (next !== savedSize) {
          setSize(next);
          setSavedSize(next);
        }
      }
      const nextMax = String(rule.maxVolumeContracts);
      if (nextMax !== savedMaxVol) {
        setMaxVol(nextMax);
        setSavedMaxVol(nextMax);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      rule?.thresholdSum,
      rule?.pathA?.thresholdSum,
      rule?.pathB?.thresholdSum,
      rule?.maxVolumeContracts,
      rule?.kalshiConfig?.contractsToBuy,
      rule?.polymarketConfig?.contractsToBuy
    ]);

    // Re-apply the bulk seed on each Apply for RULE-LESS rows only (existing
    // rules are updated server-side by the bulk endpoint + the refetch-sync
    // effect above). Setting BOTH the value and its `saved*` counterpart keeps
    // the staged value clean — no create-✓ appears and no rule auto-creates;
    // it's only staged for a later manual arm.
    useEffect(() => {
      if (rule) return;
      if (bulkApplyNonce === 0) return;
      if (bulkSeed.threshold !== undefined) {
        setThreshold(bulkSeed.threshold);
        setSavedThreshold(bulkSeed.threshold);
      }
      if (bulkSeed.size !== undefined) {
        setSize(bulkSeed.size);
        setSavedSize(bulkSeed.size);
      }
      if (bulkSeed.maxVol !== undefined) {
        setMaxVol(bulkSeed.maxVol);
        setSavedMaxVol(bulkSeed.maxVol);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bulkApplyNonce]);

    // Counter-hedge pricing mode is STAGED (like threshold/size), NOT
    // fire-on-change: the selector edits `pendingHedgeMode` and the per-line
    // check button persists it via `handleCommit`. `null` on the rule = inherit
    // the global default, mapped to/from the Select's sentinel. Defined before
    // `isDirty` so the check button surfaces on a hedge-only edit too.
    const hedgeBaseline: HedgeModeSelectValue = rule
      ? toSelectValue(rule.counterHedgeMode)
      : INHERIT_SENTINEL;
    const hedgeModeValue: HedgeModeSelectValue = pendingHedgeMode;
    const hedgeDirty = pendingHedgeMode !== hedgeBaseline;

    // Maker size mode is STAGED too (same model as hedge): the selector edits
    // `pendingSizeMode` and the per-line check button persists it, instead of a
    // fire-on-change PATCH the operator can't confirm.
    const sizeModeBaseline: 'dynamic' | 'fixed' = rule
      ? rule.makerSizeMode === 'dynamic'
        ? 'dynamic'
        : 'fixed'
      : 'fixed';
    const makerSizeMode: 'dynamic' | 'fixed' = pendingSizeMode;
    const sizeModeDirty = pendingSizeMode !== sizeModeBaseline;

    const isDirty =
      threshold !== savedThreshold ||
      size !== savedSize ||
      maxVol !== savedMaxVol ||
      hedgeDirty ||
      sizeModeDirty;

    // Wait / Gate / Sweep / Walk drafts — all staged behind the per-row ✓.
    // The Gate column is split into two independent per-platform checkboxes
    // (Poly / Kalshi), each with its own dirty state + tint.
    const lockstepDirty = pendingLockstep !== lockstepBaseline;
    const polyGateDirty = pendingPolyGate !== polyGateBaseline;
    const kalshiGateDirty = pendingKalshiGate !== kalshiGateBaseline;
    const sweepDirty = pendingSweep !== sweepBaseline;
    const walkSecsDirty = isWalkFieldDirty(walkSecsInput, savedWalkSecs);
    const walkCentsDirty = isWalkFieldDirty(walkCentsInput, savedWalkCents);
    const walkDirty = walkSecsDirty || walkCentsDirty;

    // Whole-row dirty: ANY Parameters-group draft differs from its committed
    // value. Drives the per-row ✓ confirm button (highlighted when dirty).
    // A rule-less row that only toggled the "> $1" opt-in is still dirty, so the
    // ✓ can create a paused rule carrying the flag. Existing rules commit the
    // flag immediately via the dedicated action (below), not the ✓.
    const allowAboveOneDirty = !rule && pendingAllowAboveOne;

    const rowParamsDirty =
      isDirty ||
      lockstepDirty ||
      polyGateDirty ||
      kalshiGateDirty ||
      sweepDirty ||
      walkDirty ||
      allowAboveOneDirty;

    const validateThreshold = useCallback(
      (value: string): string | null => {
        const n = Number.parseFloat(value);
        if (!Number.isFinite(n) || n <= 0) return 'Must be > 0';
        if (!isThresholdWithinBound(n, tBound)) {
          return tBound.inclusive
            ? `Must be ≤ ${tBound.max.toFixed(2)}`
            : 'Must be < 1.00 — enable > $1 for this pair';
        }
        return null;
      },
      [tBound]
    );

    useEffect(() => {
      setThresholdError(validateThreshold(threshold));
    }, [threshold, validateThreshold]);

    // Pre-rule commit: typing values with no rule creates a PAUSED rule
    // carrying those values. No maker flag is set — the row lands paused
    // with all 3 switches visually off (rule flags are all false on
    // creation). The operator later activates via a switch.
    const handleCreatePausedRule = useCallback(async () => {
      if (!rowParamsDirty || rule || !pair._id) return;
      const tErr = validateThreshold(threshold);
      if (tErr) {
        setThresholdError(tErr);
        return;
      }
      const parsedThr = Number.parseFloat(threshold);
      const parsedSize = Number.parseInt(size, 10);
      const parsedMaxV = Number.parseFloat(maxVol);
      if (!isThresholdWithinBound(parsedThr, tBound)) return;
      if (!Number.isFinite(parsedSize) || parsedSize <= 0) return;
      if (!Number.isFinite(parsedMaxV) || parsedMaxV <= 0) return;
      const parsedWalkSecs =
        walkSecsInput.trim() !== '' && Number.isInteger(Number(walkSecsInput)) && Number(walkSecsInput) >= 1
          ? Number(walkSecsInput)
          : null;
      const parsedWalkCents =
        walkCentsInput.trim() !== '' && Number.isInteger(Number(walkCentsInput)) && Number(walkCentsInput) >= 1
          ? Number(walkCentsInput)
          : null;
      setSaving(true);
      try {
        await onCreateRule({
          pair,
          makerFlag: null,
          tabPath: path,
          thresholdSum: parsedThr,
          size: parsedSize,
          maxVolumeContracts: parsedMaxV,
          ...buildPendingModeFields(
            pendingSizeMode,
            pendingHedgeMode,
            null
          ),
          counterMakerWalkStepSeconds: parsedWalkSecs,
          counterMakerWalkStepCents: parsedWalkCents,
          // Seed the Wait / Gate / Sweep drafts into the paused create so a
          // later arm picks up the operator's staged choices.
          lockstepMaxAheadBatches: pendingLockstep ? 0 : null,
          disablePlacementGatePolymarket: pendingPolyGate ? true : null,
          disablePlacementGateKalshi: pendingKalshiGate ? true : null,
          sweepDualPostEnabled: pendingSweep,
          // The "> $1" opt-in rides the create (the backend spreads it onto the
          // new paused rule) so a rule-less row can be born already opted in.
          allowThresholdAboveOne: pendingAllowAboveOne,
        });
        setSavedThreshold(threshold);
        setSavedSize(size);
        setSavedMaxVol(maxVol);
        setSavedWalkSecs(walkSecsInput);
        setSavedWalkCents(walkCentsInput);
      } finally {
        setSaving(false);
      }
    }, [
      rowParamsDirty,
      rule,
      pair,
      path,
      threshold,
      size,
      maxVol,
      validateThreshold,
      tBound,
      onCreateRule,
      pendingSizeMode,
      pendingHedgeMode,
      walkSecsInput,
      walkCentsInput,
      pendingLockstep,
      pendingPolyGate,
      pendingKalshiGate,
      pendingSweep,
      pendingAllowAboveOne,
    ]);

    // Per-row batch confirm (✓). Commits EVERY changed Parameters-group draft
    // (Thr / Size / MaxV / Mode / Hedge / Wait / Gate / Sweep / Walk-s /
    // Walk-¢) in ONE PATCH — only the keys that actually differ from the rule's
    // committed value — then clears the row's drafts. For a rule-less row it
    // falls through to the paused-create path (which already seeds all pending
    // fields into the create payload). Uses the SAME DTO mapping each field
    // commits today: Thr→pathA/BThresholdSum, Size→kalshi/polymarketConfig
    // .contractsToBuy, MaxV→maxVolumeContracts, Mode→makerSizeMode,
    // Hedge→counterHedgeMode, Wait→lockstepMaxAheadBatches (0 | null),
    // Gate→disablePlacementGate{Polymarket,Kalshi}, Sweep→sweepDualPostEnabled,
    // Walk→counterMakerWalkStep{Seconds,Cents}.
    const handleConfirmRow = useCallback(async () => {
      if (!rowParamsDirty) return;
      if (!rule) {
        await handleCreatePausedRule();
        return;
      }
      const dto: UpdateEntryRuleDto = {};

      if (
        threshold !== savedThreshold ||
        size !== savedSize ||
        maxVol !== savedMaxVol
      ) {
        const tErr = validateThreshold(threshold);
        if (tErr) {
          setThresholdError(tErr);
          return;
        }
      }
      const parsedThreshold = Number.parseFloat(threshold);
      const parsedSize = Number.parseInt(size, 10);
      const parsedMaxVol = Number.parseFloat(maxVol);
      if (
        isThresholdWithinBound(parsedThreshold, tBound) &&
        threshold !== savedThreshold
      ) {
        if (path === 'a') dto.pathAThresholdSum = parsedThreshold;
        else dto.pathBThresholdSum = parsedThreshold;
      }
      if (
        Number.isFinite(parsedMaxVol) &&
        parsedMaxVol > 0 &&
        maxVol !== savedMaxVol
      ) {
        dto.maxVolumeContracts = parsedMaxVol;
      }
      if (Number.isFinite(parsedSize) && parsedSize > 0 && size !== savedSize) {
        if (rule.kalshiConfig) {
          dto.kalshiConfig = {
            side: rule.kalshiConfig.side,
            ticker: rule.kalshiConfig.ticker,
            contractsToBuy: parsedSize
          };
        }
        if (rule.polymarketConfig) {
          dto.polymarketConfig = {
            side: rule.polymarketConfig.side,
            yesTokenId: rule.polymarketConfig.yesTokenId,
            noTokenId: rule.polymarketConfig.noTokenId,
            conditionId: rule.polymarketConfig.conditionId,
            contractsToBuy: parsedSize
          };
        }
      }
      if (hedgeDirty) {
        dto.counterHedgeMode = fromSelectValue(pendingHedgeMode);
      }
      if (sizeModeDirty) {
        dto.makerSizeMode = pendingSizeMode;
      }
      if (lockstepDirty) {
        // Checked = strict lockstep (0 batches ahead); unchecked = inherit
        // the group fallback (null).
        dto.lockstepMaxAheadBatches = pendingLockstep ? 0 : null;
      }
      if (polyGateDirty) {
        dto.disablePlacementGatePolymarket = pendingPolyGate;
      }
      if (kalshiGateDirty) {
        dto.disablePlacementGateKalshi = pendingKalshiGate;
      }
      if (sweepDirty) {
        dto.sweepDualPostEnabled = pendingSweep;
      }
      if (walkDirty) {
        const secsVal = parseWalkInput(walkSecsInput);
        const centsVal = parseWalkInput(walkCentsInput);
        // Invalid walk input → drop the whole confirm (the operator must fix
        // it) rather than silently shipping a partial PATCH.
        if (secsVal === undefined || centsVal === undefined) return;
        dto.counterMakerWalkStepSeconds = secsVal;
        dto.counterMakerWalkStepCents = centsVal;
      }

      // Nothing actually mappable changed (e.g. a no-op edit that reverted).
      if (Object.keys(dto).length === 0) {
        setSavedThreshold(threshold);
        setSavedSize(size);
        setSavedMaxVol(maxVol);
        setSavedWalkSecs(walkSecsInput);
        setSavedWalkCents(walkCentsInput);
        return;
      }

      setSaving(true);
      const ok = await onUpdateRule(rule, dto);
      if (ok) {
        setSavedThreshold(threshold);
        setSavedSize(size);
        setSavedMaxVol(maxVol);
        setSavedWalkSecs(walkSecsInput);
        setSavedWalkCents(walkCentsInput);
        // Mode/Hedge/Wait/Gate/Sweep drafts re-sync to the rule's new value
        // via the keyed effects above; leaving them as-is keeps the committed
        // draft = baseline (clean) until the refetch lands.
      }
      setSaving(false);
    }, [
      rowParamsDirty,
      rule,
      handleCreatePausedRule,
      path,
      threshold,
      size,
      maxVol,
      savedThreshold,
      savedSize,
      savedMaxVol,
      hedgeDirty,
      pendingHedgeMode,
      sizeModeDirty,
      pendingSizeMode,
      lockstepDirty,
      pendingLockstep,
      polyGateDirty,
      pendingPolyGate,
      kalshiGateDirty,
      pendingKalshiGate,
      sweepDirty,
      pendingSweep,
      walkDirty,
      walkSecsInput,
      walkCentsInput,
      onUpdateRule,
      validateThreshold,
      tBound
    ]);

    // Maker size mode (fixed vs dynamic). 'fixed' (default) rests at the
    // rule's Size param; 'dynamic' sizes the resting maker to the counter
    // top-of-book. Persisted immediately on change via the shared per-rule
    // update path.
    // Staged on change; persisted by the per-line check button (handleCommit).
    const handleMakerSizeModeChange = useCallback((next: 'dynamic' | 'fixed') => {
      setPendingSizeMode(next);
    }, []);

    // Staged on change; persisted by the per-line check button (handleCommit
    // → handleSave for an existing rule, or handleCreatePausedRule which seeds
    // pendingHedgeMode into the create payload for a rule-less row).
    const handleHedgeModeChange = useCallback((next: HedgeModeSelectValue) => {
      setPendingHedgeMode(next);
    }, []);

    // Sync walk inputs when the rule updates from the server.
    useEffect(() => {
      const nextSecs =
        rule?.counterMakerWalkStepSeconds != null
          ? String(rule.counterMakerWalkStepSeconds)
          : '';
      const nextCents =
        rule?.counterMakerWalkStepCents != null
          ? String(rule.counterMakerWalkStepCents)
          : '';
      setWalkSecsInput(nextSecs);
      setWalkCentsInput(nextCents);
      setSavedWalkSecs(nextSecs);
      setSavedWalkCents(nextCents);
    }, [rule?.counterMakerWalkStepSeconds, rule?.counterMakerWalkStepCents]);

    // Re-sync the staged hedge mode when the rule's value changes server-side
    // (create / save / external edit). Keyed on the VALUE, not the rule object,
    // so a routine poll that returns the same mode never clobbers an in-flight
    // staged edit the operator hasn't committed yet.
    useEffect(() => {
      setPendingHedgeMode(
        rule ? toSelectValue(rule.counterHedgeMode) : INHERIT_SENTINEL
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rule?.counterHedgeMode]);

    // Same staged re-sync for maker size mode (keyed on the value, not the rule
    // object, so a poll never clobbers an in-flight staged edit).
    useEffect(() => {
      setPendingSizeMode(rule?.makerSizeMode === 'dynamic' ? 'dynamic' : 'fixed');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rule?.makerSizeMode]);

    // Re-sync the staged Wait / Gate / Sweep drafts when the rule's server
    // value changes (create / save / external edit). Keyed on each VALUE so a
    // routine poll returning the same value never clobbers an in-flight draft.
    useEffect(() => {
      setPendingLockstep(rule?.lockstepMaxAheadBatches === 0);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rule?.lockstepMaxAheadBatches]);
    useEffect(() => {
      setPendingPolyGate(rule?.disablePlacementGatePolymarket === true);
    }, [rule?.disablePlacementGatePolymarket]);
    useEffect(() => {
      setPendingKalshiGate(rule?.disablePlacementGateKalshi === true);
    }, [rule?.disablePlacementGateKalshi]);
    useEffect(() => {
      setPendingSweep(rule?.sweepDualPostEnabled === true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rule?.sweepDualPostEnabled]);

    // Every rule now owns BOTH arb paths (the bidirectional toggle + the
    // cross-tab lock were removed). All 4 maker switches bind to live rule
    // flags on BOTH tabs, with no path-classification narrowing.
    const ruleBelongsToThisTab = true;

    // Market data + math — always tracks the TAB's path, never a
    // "lower-sum" hint. The other tab owns the other path. All sim
    // numbers come straight from the backend `engineSim` payload.
    const metrics = computePathMetrics(path, liveState);
    const {
      polyAsk,
      polyBid,
      kalshiAsk,
      kalshiBid,
      polyAllIn,
      kalshiAllIn,
      polyMakerRest,
      kalshiMakerRest,
      polyMakerPlace,
      kalshiMakerPlace,
      sumTT,
      sumPolyMk,
      sumKalshiMk,
      tnaTT,
      tnaPolyMk,
      tnaKalshiMk
    } = metrics;

    // Qty cells follow the same kill-switch gate as the price metrics:
    // when the opener is off for this pair, show no size either.
    const qtyState = pairOpenerOn ? liveState : undefined;
    const polyAskSize = (
      path === 'a' ? qtyState?.polyYesAsk : qtyState?.polyNoAsk
    )?.size;
    const polyBidSize = (
      path === 'a' ? qtyState?.polyYesBid : qtyState?.polyNoBid
    )?.size;
    const kalshiAskSize = (
      path === 'a' ? qtyState?.kalshiNoAsk : qtyState?.kalshiYesAsk
    )?.size;
    const kalshiBidSize = (
      path === 'a' ? qtyState?.kalshiNoBid : qtyState?.kalshiYesBid
    )?.size;

    const thresholdNum = Number.parseFloat(threshold);
    const thresholdValid = isThresholdWithinBound(thresholdNum, tBound);
    // Advisory (non-blocking): an opted-in / mode-driven row may rest above $1.
    const thresholdAboveOne =
      tBound.aboveOneAllowed &&
      Number.isFinite(thresholdNum) &&
      thresholdNum > 1;

    // Switch → flag mapping for this tab's path.
    const polyFlag = TAB_FLAGS[path].poly;
    const kalshiFlag = TAB_FLAGS[path].kalshi;

    // Switch display state: only bind to rule flags if the rule belongs
    // to this tab's path. Off otherwise — the OTHER tab owns it.
    const polyMakerOn = ruleBelongsToThisTab && rule?.[polyFlag] === true;
    const kalshiMakerOn = ruleBelongsToThisTab && rule?.[kalshiFlag] === true;
    // Groups-only trading gate: arming a maker flag on an UNGROUPED pair is
    // a guaranteed backend 400, so the OFF switch is disabled with a tooltip
    // instead. Turning OFF is never blocked (see activationGate.ts).
    const polyActivationBlocked = isActivationBlocked(pair, polyMakerOn);
    const kalshiActivationBlocked = isActivationBlocked(pair, kalshiMakerOn);
    // A pending click wins over the rule's flag so the switch reflects the
    // operator's last action immediately while the PATCH(es) are in flight.
    const polySwitch = resolveMakerSwitchState({
      pending: pendingFlags[polyFlag],
      makerOn: polyMakerOn,
      activationBlocked: polyActivationBlocked,
    });
    const kalshiSwitch = resolveMakerSwitchState({
      pending: pendingFlags[kalshiFlag],
      makerOn: kalshiMakerOn,
      activationBlocked: kalshiActivationBlocked,
    });
    const ttOn =
      ruleBelongsToThisTab &&
      (anyRule?.entryStrategy ?? 'maker-first') === 'taker-taker';

    const inSx = {
      '& .MuiInputBase-input': {
        fontSize: '0.62rem',
        py: 0.15,
        px: 0.2,
        textAlign: 'right',
        width: 28
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255,255,255,0.08)'
      },
      minWidth: 0
    } as const;

    // Threshold gets a wider, slightly larger input than Size/MaxV — it is
    // the field the operator tunes most and needs to read at a glance.
    const thrInSx = {
      '& .MuiInputBase-input': {
        fontSize: '0.66rem',
        py: 0.15,
        px: 0.3,
        textAlign: 'right',
        width: 30
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255,255,255,0.08)'
      },
      minWidth: 0
    } as const;

    // The red hard-cap row outline is gone. Hover highlight stays.
    const rowSx = { '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } };

    // Apply a maker-flag change. Every rule owns both arb paths now, so
    // arming one leg NEVER clears the other path's flags — all 4 maker
    // switches are independently armable.
    const doSetMakerFlag = useCallback(
      async (flag: MakerFlag, value: boolean) => {
        setPendingFlags(p => ({ ...p, [flag]: value }));
        try {
          // Arming a leg requires the pair's opener kill switch to be ON, or
          // the backend rejects activation (`assertPairOpenerEnabled` → 400)
          // and the optimistic switch snaps back to OFF. Auto-enable the pair
          // in the same gesture (operator's choice) BEFORE the arm PATCH so it
          // reads the fresh `openerEnabled: true`. Idempotent: a no-op when
          // already on.
          if (needsOpenerAutoEnable(pair, value) && pair._id) {
            await setPairOpenerEnabled.mutateAsync({
              pairId: pair._id,
              enabled: true
            });
          }
          if (rule) {
            await onSetMakerFlag(rule, flag, value, []);
          } else if (value) {
            const tErr = validateThreshold(threshold);
            if (tErr) {
              setThresholdError(tErr);
              return;
            }
            const parsedThr = Number.parseFloat(threshold);
            const parsedSize = Number.parseInt(size, 10);
            const parsedMaxV = Number.parseFloat(maxVol);
            // Ensure-then-patch: the create is single-flight per pair (a
            // concurrent create from Enter/another switch is joined, not
            // duplicated) and never carries the flag — applying it via
            // the normal PATCH path below preserves the false→true
            // activation edge the backend keys on.
            const created = await onCreateRule({
              pair,
              makerFlag: flag,
              tabPath: path,
              thresholdSum:
                isThresholdWithinBound(parsedThr, tBound)
                  ? parsedThr
                  : DEFAULT_THRESHOLD,
              size:
                Number.isFinite(parsedSize) && parsedSize > 0
                  ? parsedSize
                  : DEFAULT_SIZE,
              maxVolumeContracts:
                Number.isFinite(parsedMaxV) && parsedMaxV > 0
                  ? parsedMaxV
                  : DEFAULT_MAX_VOL,
              ...buildPendingModeFields(
                pendingSizeMode,
                pendingHedgeMode,
                null
              ),
              counterMakerWalkStepSeconds:
                walkSecsInput.trim() !== '' && Number.isInteger(Number(walkSecsInput)) && Number(walkSecsInput) >= 1
                  ? Number(walkSecsInput)
                  : null,
              counterMakerWalkStepCents:
                walkCentsInput.trim() !== '' && Number.isInteger(Number(walkCentsInput)) && Number(walkCentsInput) >= 1
                  ? Number(walkCentsInput)
                  : null,
              lockstepMaxAheadBatches: pendingLockstep === true ? 0 : null,
              disablePlacementGatePolymarket: pendingPolyGate ? true : null,
              disablePlacementGateKalshi: pendingKalshiGate ? true : null,
              // Forward the staged "> $1" opt-in so a >$1 threshold armed via
              // the maker switch (not the ✓) is not dropped by EntryPage's
              // flag-aware layer-3 create guard.
              allowThresholdAboveOne: allowAboveOne,
            });
            if (created) {
              await onSetMakerFlag(created, flag, value, []);
            }
          }
        } finally {
          // Drop the optimistic overlay the moment the mutation settles. By
          // now the rule cache holds server truth on success (useSetMakerFlag
          // keeps its optimistic patch until the onSettled refetch lands) or
          // the rolled-back value on error, so the switch hands off to the
          // real flag value without flickering.
          setPendingFlags(p => {
            if (!(flag in p)) return p;
            const next = { ...p };
            delete next[flag];
            return next;
          });
        }
      },
      [
        rule,
        threshold,
        size,
        maxVol,
        pair,
        path,
        onCreateRule,
        onSetMakerFlag,
        setPairOpenerEnabled,
        validateThreshold,
        tBound,
        pendingSizeMode,
        pendingHedgeMode,
        walkSecsInput,
        walkCentsInput,
        pendingLockstep,
        pendingPolyGate,
        pendingKalshiGate,
        allowAboveOne,
      ]
    );

    const handleTTToggle = useCallback(
      async (value: boolean) => {
        setTogglingTT(true);
        try {
          if (anyRule) {
            // TT is a per-rule entry-strategy toggle, independent of path.
            await onUpdateRule(anyRule, {
              entryStrategy: value ? 'taker-taker' : 'maker-first'
            });
            return;
          }
          if (!value) return;
          const tErr = validateThreshold(threshold);
          if (tErr) {
            setThresholdError(tErr);
            return;
          }
          const parsedThr = Number.parseFloat(threshold);
          const parsedSize = Number.parseInt(size, 10);
          const parsedMaxV = Number.parseFloat(maxVol);
          // Ensure-then-patch: single-flight create (joins any concurrent
          // create for this pair), then the strategy lands via the normal
          // update path instead of being baked into the create.
          const created = await onCreateRule({
            pair,
            makerFlag: null,
            tabPath: path,
            thresholdSum:
              isThresholdWithinBound(parsedThr, tBound)
                ? parsedThr
                : DEFAULT_THRESHOLD,
            size:
              Number.isFinite(parsedSize) && parsedSize > 0
                ? parsedSize
                : DEFAULT_SIZE,
            maxVolumeContracts:
              Number.isFinite(parsedMaxV) && parsedMaxV > 0
                ? parsedMaxV
                : DEFAULT_MAX_VOL,
            ...buildPendingModeFields(
              pendingSizeMode,
              pendingHedgeMode,
              null
            ),
            counterMakerWalkStepSeconds:
              walkSecsInput.trim() !== '' && Number.isInteger(Number(walkSecsInput)) && Number(walkSecsInput) >= 1
                ? Number(walkSecsInput)
                : null,
            counterMakerWalkStepCents:
              walkCentsInput.trim() !== '' && Number.isInteger(Number(walkCentsInput)) && Number(walkCentsInput) >= 1
                ? Number(walkCentsInput)
                : null,
            lockstepMaxAheadBatches: pendingLockstep === true ? 0 : null,
            disablePlacementGatePolymarket: pendingPolyGate ? true : null,
            disablePlacementGateKalshi: pendingKalshiGate ? true : null,
            // Forward the staged "> $1" opt-in (see the maker-switch create).
            allowThresholdAboveOne: allowAboveOne,
          });
          if (created) {
            await onUpdateRule(created, { entryStrategy: 'taker-taker' });
          }
        } finally {
          setTimeout(() => setTogglingTT(false), 2000);
        }
      },
      [
        anyRule,
        pair,
        path,
        threshold,
        size,
        maxVol,
        onCreateRule,
        onUpdateRule,
        validateThreshold,
        tBound,
        pendingSizeMode,
        pendingHedgeMode,
        walkSecsInput,
        walkCentsInput,
        pendingLockstep,
        pendingPolyGate,
        pendingKalshiGate,
        allowAboveOne,
      ]
    );

    const ruleStatusColor =
      anyRule?.status === 'active'
        ? '#4caf50'
        : anyRule?.status === 'paused'
          ? '#ff9800'
          : 'grey.600';

    // Sum cell color: red if over hard cap (informational only — the
    // backend gate is the safety contract); green if at or below the
    // operator's threshold; default blue otherwise.
    const sumColor = (sum: number | null): string => {
      if (sum == null) return 'grey.500';
      if (sum >= ENTRY_HARD_CAP_GROSS) return '#f44336';
      if (thresholdValid && sum <= thresholdNum) return '#4caf50';
      return '#42a5f5';
    };
    const tnaColor = (tna: number | null): string => {
      if (tna == null) return 'grey.500';
      return tna >= 0 ? '#4caf50' : '#f44336';
    };

    const polyCategoryFeePct = (
      polymarketTakerFeeRate(rule?.category) * 100
    ).toFixed(1);

    return (
      <>
        <TableRow ref={measureRef} data-index={dataIndex} sx={rowSx}>
          {/* Bulk select */}
          <TableCell
            sx={{ ...cSx, px: 0, verticalAlign: 'middle' }}
            align="center"
            padding="none"
            onClick={e => e.stopPropagation()}
          >
            <Checkbox
              size="small"
              checked={selected}
              disabled={!pair._id}
              onChange={() => pair._id && onToggleSelect(pair._id)}
              sx={{
                p: 0,
                color: 'grey.600',
                '& .MuiSvgIcon-root': { fontSize: 14 }
              }}
            />
          </TableCell>
          {/* Market */}
          <TableCell sx={{ ...cSx, overflow: 'hidden', pl: 0.1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.2 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: ruleStatusColor,
                  mt: 0.5,
                  ml: 0.2,
                  flexShrink: 0
                }}
              />
              <Switch
                size="small"
                checked={armSwitch.checked}
                disabled={armSwitch.disabled}
                onChange={e => {
                  e.stopPropagation();
                  void handleOpenerToggle(e.target.checked);
                }}
                onClick={e => e.stopPropagation()}
                disableRipple
                sx={{
                  m: 0,
                  p: 0,
                  width: 18,
                  height: 10,
                  display: 'inline-flex',
                  '& .MuiSwitch-switchBase': {
                    p: '1px',
                    '&.Mui-checked': {
                      transform: 'translateX(8px)',
                      color: '#fff',
                      '& + .MuiSwitch-track': {
                        backgroundColor: '#7c3aed',
                        opacity: 1
                      }
                    }
                  },
                  '& .MuiSwitch-thumb': {
                    width: 8,
                    height: 8,
                    boxShadow: 'none'
                  },
                  '& .MuiSwitch-track': {
                    borderRadius: 5,
                    backgroundColor: 'rgba(255,255,255,0.18)',
                    opacity: 1
                  }
                }}
              />
              {armSwitch.label && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.55rem',
                    lineHeight: 1,
                    ml: 0.3,
                    color: armSwitch.label === 'Apagando…' ? '#ff9800' : '#7c3aed',
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}
                >
                  {armSwitch.label}
                </Typography>
              )}
              <IconButton
                size="small"
                disabled={hidePending}
                onClick={async e => {
                  e.stopPropagation();
                  setHidePending(true);
                  try {
                    await onHidePair(pair);
                  } catch {
                    // The mutation's onError rolls the optimistic cache
                    // back; nothing else to do here.
                  } finally {
                    setHidePending(false);
                  }
                }}
                sx={{
                  p: 0,
                  ml: 0.4,
                  color: hidePending ? 'grey.700' : 'grey.500',
                  '&:hover': { color: '#f44336' },
                  flexShrink: 0
                }}
              >
                {hidePending ? (
                  <CircularProgress size={12} />
                ) : (
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                )}
              </IconButton>
              {/* "> $1" enablement — the universal per-pair gate for crossing $1
                  (every hedge mode). ALWAYS shown, like every other param: an
                  existing rule commits immediately via the dedicated action; a
                  rule-less / not-yet-loaded row stages it in the draft and
                  carries it into the create. Outlined/grey when off, amber-filled
                  when on. Sits beside the trash so it never crowds the THR input. */}
              {
                <Tooltip title={allowAboveOneTooltip} arrow placement="top">
                  <Box
                    component="button"
                    type="button"
                    aria-label={
                      allowAboveOne
                        ? 'Disable threshold above $1 for this pair'
                        : 'Allow threshold above $1 for this pair'
                    }
                    disabled={!!rule && setAllowThresholdAboveOne.isLoading}
                    onClick={e => {
                      e.stopPropagation();
                      if (rule) {
                        void setAllowThresholdAboveOne.mutate({
                          id: rule._id,
                          enabled: !allowAboveOne
                        });
                      } else {
                        setPendingAllowAboveOne(v => !v);
                      }
                    }}
                    sx={{
                      ml: 0.4,
                      px: 0.4,
                      height: 15,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '1px',
                      flexShrink: 0,
                      cursor: 'pointer',
                      borderRadius: '4px',
                      fontSize: '0.5rem',
                      fontWeight: 800,
                      lineHeight: 1,
                      border: '1px solid',
                      borderColor: allowAboveOne
                        ? '#ffb300'
                        : 'rgba(255,255,255,0.22)',
                      color: allowAboveOne ? '#1a1a2e' : 'grey.500',
                      backgroundColor: allowAboveOne ? '#ffb300' : 'transparent',
                      '&:hover': {
                        borderColor: '#ffb300',
                        color: allowAboveOne ? '#1a1a2e' : '#ffb300'
                      },
                      '&:disabled': { opacity: 0.5, cursor: 'default' }
                    }}
                  >
                    <KeyboardDoubleArrowUpIcon sx={{ fontSize: 11 }} />
                    &gt;$1
                  </Box>
                </Tooltip>
              }
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.15,
                  minWidth: 0,
                  flex: 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <a
                    href={
                      pair.polymarket?.marketUrl ??
                      liveState?.polymarketMarketUrl ??
                      undefined
                    }
                    target="_blank"
                    rel="noopener"
                    onClick={e => e.stopPropagation()}
                  >
                    <img
                      src="/assets/exchanges/png/100x100/polymarket.png"
                      alt="P"
                      style={{ width: 13, height: 13, borderRadius: '50%' }}
                    />
                  </a>
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {pair.polymarketTitle || pair.pairName}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <a
                    href={
                      pair.kalshi?.marketUrl ??
                      liveState?.kalshiMarketUrl ??
                      undefined
                    }
                    target="_blank"
                    rel="noopener"
                    onClick={e => e.stopPropagation()}
                  >
                    <img
                      src="/assets/exchanges/png/100x100/kalshi.png"
                      alt="K"
                      style={{ width: 13, height: 13, borderRadius: '50%' }}
                    />
                  </a>
                  <Typography
                    sx={{
                      fontSize: '0.6rem',
                      color: 'grey.400',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {pair.kalshiTitle || pair.pairName}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </TableCell>

          {/* Position contracts split by side, with Poly on top and Kalshi
              below in each cell. Renders the group's own venue read
              (account-correct) when it is fresh, the main-account-only OPEN
              prediction_positions map only on a main-account view, and `—`
              otherwise — beside an ACT `—`, never a number from another
              account. The row hover names the source. */}
          <TableCell
            sx={cSx}
            align="center"
            data-contracts-source={displayContracts.source}
            data-contracts-title={contractsTitle}
          >
            <Stack spacing={0}>
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  color: contractsColor(
                    displayContracts,
                    'poly',
                    displayContracts.polyYes
                  )
                }}
              >
                P: {fmtContracts(displayContracts.polyYes)}
              </Typography>
              <Tooltip title={contractsTitle} placement="top">
                <Typography
                  sx={{
                    fontSize: '0.6rem',
                    color: contractsColor(
                      displayContracts,
                      'kalshi',
                      displayContracts.kalshiYes
                    )
                  }}
                >
                  K: {fmtContracts(displayContracts.kalshiYes)}
                </Typography>
              </Tooltip>
            </Stack>
          </TableCell>
          <TableCell
            sx={cSx}
            align="center"
            data-contracts-source={displayContracts.source}
            data-contracts-title={contractsTitle}
          >
            <Stack spacing={0}>
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  color: contractsColor(
                    displayContracts,
                    'poly',
                    displayContracts.polyNo
                  )
                }}
              >
                P: {fmtContracts(displayContracts.polyNo)}
              </Typography>
              <Tooltip title={contractsTitle} placement="top">
                <Typography
                  sx={{
                    fontSize: '0.6rem',
                    color: contractsColor(
                      displayContracts,
                      'kalshi',
                      displayContracts.kalshiNo
                    )
                  }}
                >
                  K: {fmtContracts(displayContracts.kalshiNo)}
                </Typography>
              </Tooltip>
            </Stack>
          </TableCell>

          {/* Actual NET — the venue-polled net when it is fresh and attributable
              to this pair, `—` with the reason when it is not, and the REST /
              engine net only on a main-account view. See `ActNetCell`. */}
          <TableCell sx={cSx} align="right">
            <Box sx={{ fontSize: '0.6rem', fontWeight: 700 }}>
              <ActNetCell
                ruleCost={liveState?.ruleCost}
                view={venueView}
                status={venueStatus}
                rest={{
                  positions: (
                    [
                      { platform: 'polymarket', side: 'yes', contracts: contracts.polyYes },
                      { platform: 'polymarket', side: 'no', contracts: contracts.polyNo },
                      { platform: 'kalshi', side: 'yes', contracts: contracts.kalshiYes },
                      { platform: 'kalshi', side: 'no', contracts: contracts.kalshiNo },
                    ] as NonNullable<RestNetSource['positions']>
                  ).filter((p) => p.contracts > 0),
                }}
              />
            </Box>
          </TableCell>

          {/* Polymarket — Qty · Bid · Ask · Qty */}
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
              {fmtQty(polyBidSize)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.62rem', color: 'grey.300' }}>
              {fmtPrice(polyBid)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 600 }}>
              {fmtPrice(polyAsk)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
              {fmtQty(polyAskSize)}
            </Typography>
          </TableCell>

          {/* Kalshi — Qty · Bid · Ask · Qty */}
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
              {fmtQty(kalshiBidSize)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.62rem', color: 'grey.300' }}>
              {fmtPrice(kalshiBid)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 600 }}>
              {fmtPrice(kalshiAsk)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
              {fmtQty(kalshiAskSize)}
            </Typography>
          </TableCell>

          {/* Parameters — all staged into the per-row draft; the per-row ✓
              (after Walk) commits every changed field in ONE PATCH. Dirty
              inputs get a subtle blue accent so the operator can see what
              hasn't been confirmed yet. Enter on any field commits the row. */}
          <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0}
              justifyContent="flex-end"
            >
              {/* The "> $1" enablement lives next to the row ✓ (see below); here
                  the input just signals an above-$1 value by going amber/bold —
                  no wrapping advisory text. The real validation error (value
                  blocked because the pair isn't enabled) still shows inline. */}
              <TextField
                variant="outlined"
                size="small"
                value={threshold}
                placeholder="0.95"
                onChange={e => setThreshold(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleConfirmRow();
                }}
                error={!!thresholdError}
                helperText={thresholdError ?? ''}
                sx={[
                  dirtyInputSx(thrInSx, threshold !== savedThreshold),
                  thresholdAboveOne && ABOVE_ONE_THR_SX
                ]}
                inputProps={{ step: 0.01, min: 0, max: thresholdInputMax(tBound) }}
              />
            </Stack>
          </TableCell>
          <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0}
              justifyContent="flex-end"
            >
              <TextField
                variant="outlined"
                size="small"
                value={size}
                placeholder="50"
                onChange={e => setSize(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleConfirmRow();
                }}
                sx={dirtyInputSx(inSx, size !== savedSize)}
                inputProps={{ step: 1, min: 1 }}
              />
            </Stack>
          </TableCell>
          <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0}
              justifyContent="flex-end"
            >
              <TextField
                variant="outlined"
                size="small"
                value={maxVol}
                placeholder="500"
                onChange={e => setMaxVol(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleConfirmRow();
                }}
                sx={dirtyInputSx(inSx, maxVol !== savedMaxVol)}
                inputProps={{ step: 100, min: 0 }}
              />
              {(() => {
                // maxV-hit marker for THIS tab's path (either leg capped → the
                // engine disarmed it and the rule persisted the marker).
                const hit = liveState?.maxVolumeHit?.[path];
                if (!hit || (!hit.kalshi && !hit.polymarket)) return null;
                const legs = [
                  hit.polymarket ? 'Polymarket' : null,
                  hit.kalshi ? 'Kalshi' : null
                ]
                  .filter(Boolean)
                  .join(' + ');
                return (
                  <WarningAmberIcon
                    aria-label={`Max volume reached — ${legs} maker leg paused`}
                    sx={{ fontSize: 14, color: '#ff9800', ml: 0.25 }}
                  />
                );
              })()}
            </Stack>
          </TableCell>

          {/* Maker size mode — Dynamic (size to counter top-of-book) vs
              Fixed (rest at the rule's Size param). Disabled until a rule exists. */}
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0}
              justifyContent="center"
            >
              <Select
                size="small"
                value={makerSizeMode}
                disabled={saving}
                onChange={e =>
                  handleMakerSizeModeChange(
                    e.target.value as 'dynamic' | 'fixed'
                  )
                }
                // Closed selector shows a compact "D"/"F" to save
                // horizontal space; the dropdown keeps full words.
                renderValue={v => (v === 'fixed' ? 'F' : 'D')}
                sx={{
                  fontSize: '0.68rem',
                  width: '100%',
                  minWidth: 0,
                  '& .MuiSelect-select': {
                    py: 0.2,
                    pl: 0.5,
                    pr: '15px !important',
                    textAlign: 'left'
                  },
                  '& .MuiSelect-icon': { right: 0, fontSize: '0.95rem' },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: sizeModeDirty
                      ? '#42a5f5'
                      : 'rgba(255,255,255,0.08)'
                  }
                }}
              >
                <MenuItem value="dynamic" sx={{ fontSize: '0.7rem' }}>
                  Dynamic
                </MenuItem>
                <MenuItem value="fixed" sx={{ fontSize: '0.7rem' }}>
                  Fixed
                </MenuItem>
              </Select>
            </Stack>
          </TableCell>

          {/* Counter-hedge pricing mode — per-rule override of the global
              `counterHedgeModeDefault`. "Inherit" sends null. Disabled until a
              rule exists. */}
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0}
              justifyContent="center"
            >
              <Select
                size="small"
                value={hedgeModeValue}
                disabled={saving}
                onChange={e =>
                  handleHedgeModeChange(e.target.value as HedgeModeSelectValue)
                }
                // Closed selector shows the compact short label
                // (Glo/Thr/Max/Mkt); the dropdown keeps full words.
                renderValue={v => shortLabelFor(v as HedgeModeSelectValue)}
                inputProps={{ 'aria-label': 'hedge mode' }}
                sx={{
                  fontSize: '0.68rem',
                  width: '100%',
                  minWidth: 0,
                  '& .MuiSelect-select': {
                    py: 0.2,
                    pl: 0.5,
                    pr: '15px !important',
                    textAlign: 'left'
                  },
                  '& .MuiSelect-icon': { right: 0, fontSize: '0.95rem' },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: hedgeDirty
                      ? '#42a5f5'
                      : 'rgba(255,255,255,0.08)'
                  }
                }}
              >
                {hedgeOptions.map(o => (
                  <MenuItem
                    key={o.value}
                    value={o.value}
                    sx={{ fontSize: '0.7rem' }}
                  >
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </TableCell>

          {/* Lockstep (Wait): checked = strict (wait for counter fill before
              next maker). Staged into the row draft; the per-row ✓ commits it
              (checked → lockstepMaxAheadBatches: 0, unchecked → null/inherit). */}
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            <Checkbox
              size="small"
              checked={pendingLockstep}
              onChange={e => setPendingLockstep(e.target.checked)}
              sx={{
                p: 0.3,
                '& .MuiSvgIcon-root': { fontSize: 16 },
                color: lockstepDirty ? '#42a5f5' : undefined
              }}
            />
          </TableCell>

          {/* Placement gate disable: checked = keep posting far from threshold
              (illiquid). Two independent per-platform checkboxes stacked
              vertically (P = Polymarket, K = Kalshi), each staged into the row
              draft and committed by the per-row ✓, with its own dirty tint. */}
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  sx={{ fontSize: 9, color: 'grey.500', mr: 0.2 }}
                >
                  P
                </Typography>
                <Checkbox
                  size="small"
                  checked={pendingPolyGate}
                  onChange={e => setPendingPolyGate(e.target.checked)}
                  inputProps={{
                    'aria-label':
                      'Disable placement gate — Polymarket (keep posting far from threshold — illiquid)'
                  }}
                  sx={{
                    p: 0.15,
                    '& .MuiSvgIcon-root': { fontSize: 13 },
                    color: polyGateDirty ? '#42a5f5' : undefined
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                  sx={{ fontSize: 9, color: 'grey.500', mr: 0.2 }}
                >
                  K
                </Typography>
                <Checkbox
                  size="small"
                  checked={pendingKalshiGate}
                  onChange={e => setPendingKalshiGate(e.target.checked)}
                  inputProps={{
                    'aria-label':
                      'Disable placement gate — Kalshi (keep posting far from threshold — illiquid)'
                  }}
                  sx={{
                    p: 0.15,
                    '& .MuiSvgIcon-root': { fontSize: 13 },
                    color: kalshiGateDirty ? '#42a5f5' : undefined
                  }}
                />
              </Box>
            </Box>
          </TableCell>

          {/* Sweep — per-rule sweep-response (rising-leg-first) overlay. Staged
              into the row draft (like the other Parameters) and committed by
              the per-row ✓ in ONE PATCH, instead of firing immediately. */}
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            <Checkbox
              size="small"
              checked={pendingSweep}
              onChange={e => {
                e.stopPropagation();
                setPendingSweep(e.target.checked);
              }}
              inputProps={{ 'aria-label': 'sweep response enabled' }}
              sx={{
                p: 0,
                '& .MuiSvgIcon-root': { fontSize: 16 },
                color: sweepDirty ? '#42a5f5' : undefined
              }}
            />
          </TableCell>

          {/* Counter-maker walk-to-market: step interval (seconds) + step
              size (cents). Both must be set for walk to activate. Staged into
              the row draft; the per-row ✓ commits both together. */}
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={0}>
              <TextField
                size="small"
                disabled={saving}
                value={walkSecsInput}
                onChange={e => setWalkSecsInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleConfirmRow();
                }}
                inputProps={{
                  'aria-label': 'counter walk step seconds',
                  inputMode: 'numeric',
                  style: { textAlign: 'center', fontSize: '0.62rem', padding: '2px 4px' }
                }}
                sx={{
                  width: 38,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: walkSecsDirty ? '#42a5f5' : 'rgba(255,255,255,0.08)'
                  }
                }}
                placeholder="—"
              />
            </Stack>
          </TableCell>

          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={0}>
              <TextField
                size="small"
                disabled={saving}
                value={walkCentsInput}
                onChange={e => setWalkCentsInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleConfirmRow();
                }}
                inputProps={{
                  'aria-label': 'counter walk step cents',
                  inputMode: 'numeric',
                  style: { textAlign: 'center', fontSize: '0.62rem', padding: '2px 4px' }
                }}
                sx={{
                  width: 38,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: walkCentsDirty ? '#42a5f5' : 'rgba(255,255,255,0.08)'
                  }
                }}
                placeholder="—"
              />
            </Stack>
          </TableCell>

          {/* Per-row batch-confirm ✓ — commits every staged Parameters-group
              edit for this row in ONE PATCH (only the changed keys), then
              clears the row's drafts. Grey + disabled when the row is clean;
              highlighted (success green) when the row has pending edits. No
              hover tooltip (opener hovers are sim-only by policy — the color +
              disabled state already convey the dirty state). */}
          <TableCell
            sx={{ ...cSx, px: 0.1 }}
            align="center"
            onClick={e => e.stopPropagation()}
          >
            <IconButton
              size="small"
              aria-label="confirm row parameter changes"
              disabled={!rowParamsDirty || saving}
              onClick={e => {
                e.stopPropagation();
                void handleConfirmRow();
              }}
              sx={{
                p: 0.1,
                color: rowParamsDirty ? 'success.main' : 'grey.600'
              }}
            >
              {saving ? (
                <CircularProgress size={12} />
              ) : (
                <CheckIcon sx={{ fontSize: '0.85rem' }} />
              )}
            </IconButton>
          </TableCell>

          {/* Per-strategy Sim group — each strategy renders 2 cells:
              [LEGS]: stacked maker (blue) + taker (orange) prices,
                      each tagged with the platform letter (P/K) so
                      the operator never has to guess which platform a
                      number applies to. TT has no maker — both legs
                      are taker (both orange).
              [SUM/TNA]: existing sum + TNA% (unchanged).

              Mirrors the unwinder's per-strategy 2-cell layout
              (UnwindingTabSumOfSides) so reading a strategy is a
              single visual span instead of cross-column assembly. */}
          {(() => {
            // All four notes describe the backend `engineSim` payload —
            // there is no client-estimate path anymore.
            const engineSize = liveState?.engineSim?.sizeUsed;
            const polyTakerNote =
              engineSize != null
                ? `at size ${engineSize} (depth-walked VWAP)`
                : '';
            const kalshiTakerNote =
              engineSize != null
                ? `at size ${engineSize} (depth-walked VWAP)`
                : '';
            const polyMakerNote =
              'live own top-of-book bid (where a maker rests); sum = bid + kalshi all-in';
            const kalshiMakerNote =
              'live own top-of-book bid (where a maker rests); sum = bid + poly all-in';

            // Style helpers for the maker (blue) + taker (orange) cell
            // contents. The platform letter (P / K) is rendered as a
            // subscript-style chip after the price so the operator can
            // tell at a glance which platform a number applies to.
            const takerSx = {
              fontSize: '0.62rem',
              color: '#ff9800',
              cursor: 'help'
            } as const;
            const makerSx = {
              fontSize: '0.62rem',
              color: '#42a5f5',
              fontWeight: 600,
              cursor: 'help'
            } as const;
            const platformBadgeSx = {
              fontSize: '0.5rem',
              color: 'grey.500',
              fontWeight: 400,
              ml: 0.25
            } as const;

            const polyTakerTooltip =
              polyAsk == null
                ? ''
                : `Polymarket taker — top-of-book ask: ${fmtPrice(polyAsk)}. All-in ${polyTakerNote}: ${fmtPrice(polyAllIn)} (Poly fee ${polyCategoryFeePct}%).`;
            const kalshiTakerTooltip =
              kalshiAsk == null
                ? ''
                : `Kalshi taker — top-of-book ask: ${fmtPrice(kalshiAsk)}. All-in ${kalshiTakerNote}: ${fmtPrice(kalshiAllIn)} (Kalshi fee ${(KALSHI_TAKER_FEE_RATE * 100).toFixed(1)}%).`;
            const polyMakerTooltip = `Polymarket maker — own bid top: ${fmtPrice(polyMakerRest)} — ${polyMakerNote}.${
              polyMakerPlace != null
                ? ` Order rests at ${fmtPrice(polyMakerPlace)} (threshold-capped).`
                : ''
            }`;
            const kalshiMakerTooltip = `Kalshi maker — own bid top: ${fmtPrice(kalshiMakerRest)} — ${kalshiMakerNote}.${
              kalshiMakerPlace != null
                ? ` Order rests at ${fmtPrice(kalshiMakerPlace)} (threshold-capped).`
                : ''
            }`;

            // Each strategy: legs cell content + sum + tna. `topLeg` /
            // `bottomLeg` describe the two prices to render, top to
            // bottom in the legs cell. For maker-taker strategies the
            // TAKER (orange) sits on top and the MAKER (blue) below, so
            // every cell reads the same way: the leg we cross first up
            // top, our resting leg beneath it.
            type Leg = {
              price: number | null;
              raw: number | null;
              platform: 'P' | 'K';
              sx: Record<string, unknown>;
              tooltip: string;
              // Maker legs only: the threshold-capped placement price the
              // engine actually rests at, shown as `bid / place`.
              place?: number | null;
            };
            const strategies: Array<{
              key: string;
              tip: string;
              sum: number | null;
              tna: number | null;
              topLeg: Leg;
              bottomLeg: Leg;
            }> = [
              {
                key: 'tt',
                tip: 'Taker-Taker',
                sum: sumTT,
                tna: tnaTT,
                topLeg: {
                  price: polyAllIn,
                  raw: polyAsk,
                  platform: 'P',
                  sx: takerSx,
                  tooltip: polyTakerTooltip
                },
                bottomLeg: {
                  price: kalshiAllIn,
                  raw: kalshiAsk,
                  platform: 'K',
                  sx: takerSx,
                  tooltip: kalshiTakerTooltip
                }
              },
              {
                key: 'polyMk',
                tip: 'Poly maker',
                sum: sumPolyMk,
                tna: tnaPolyMk,
                // Taker (Kalshi) on top, maker (Poly) below.
                topLeg: {
                  price: kalshiAllIn,
                  raw: kalshiAsk,
                  platform: 'K',
                  sx: takerSx,
                  tooltip: kalshiTakerTooltip
                },
                bottomLeg: {
                  price: polyMakerRest,
                  raw: polyMakerRest,
                  platform: 'P',
                  sx: makerSx,
                  tooltip: polyMakerTooltip,
                  place: polyMakerPlace
                }
              },
              {
                key: 'kalshiMk',
                tip: 'Kalshi maker',
                sum: sumKalshiMk,
                tna: tnaKalshiMk,
                // Taker (Poly) on top, maker (Kalshi) below.
                topLeg: {
                  price: polyAllIn,
                  raw: polyAsk,
                  platform: 'P',
                  sx: takerSx,
                  tooltip: polyTakerTooltip
                },
                bottomLeg: {
                  price: kalshiMakerRest,
                  raw: kalshiMakerRest,
                  platform: 'K',
                  sx: makerSx,
                  tooltip: kalshiMakerTooltip,
                  place: kalshiMakerPlace
                }
              }
            ];

            const renderLeg = (leg: Leg) => (
              <Tooltip
                placement="top"
                title={leg.tooltip}
                disableHoverListener={leg.raw == null}
              >
                <Stack
                  direction="row"
                  alignItems="baseline"
                  spacing={0}
                  justifyContent="flex-end"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  <Typography sx={leg.sx}>{fmtPrice(leg.price)}</Typography>
                  <Typography component="span" sx={platformBadgeSx}>
                    {leg.platform}
                  </Typography>
                  {leg.place != null && (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '0.52rem',
                        color: 'grey.500',
                        ml: 0.25
                      }}
                    >
                      / {fmtPrice(leg.place)}
                    </Typography>
                  )}
                </Stack>
              </Tooltip>
            );

            return (
              <>
                {strategies.map(({ key, tip, sum, tna, topLeg, bottomLeg }) => (
                  <Fragment key={key}>
                    {/* Legs */}
                    <TableCell sx={simCSx} align="right">
                      <Stack spacing={0} alignItems="flex-end">
                        {renderLeg(topLeg)}
                        {renderLeg(bottomLeg)}
                      </Stack>
                    </TableCell>
                    {/* Sum + TNA */}
                    <TableCell sx={simCSx} align="right">
                      <Tooltip title={tip} placement="top">
                        <Stack spacing={0} alignItems="flex-end">
                          <Typography
                            sx={{
                              fontSize: '0.66rem',
                              fontWeight: 600,
                              color: sumColor(sum)
                            }}
                          >
                            {fmtPrice(sum)}
                          </Typography>
                          <Typography
                            sx={{ fontSize: '0.56rem', color: tnaColor(tna) }}
                          >
                            {fmtPct(tna)}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    </TableCell>
                  </Fragment>
                ))}
              </>
            );
          })()}

          {/* Ctrl — Poly maker · Kalshi maker · TT. The P/K activation
              switches carry the groups-only gate: disabled + tooltip while
              the pair is ungrouped and the flag is off. The hover listener
              is disabled while NOT blocked so the tooltip never pops over
              an operable switch (hover policy: openerHoversSimOnly). */}
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            <Tooltip
              title={UNGROUPED_ACTIVATION_TOOLTIP}
              placement="top"
              disableHoverListener={!polyActivationBlocked}
            >
              <span>
                <Switch
                  size="small"
                  sx={CTRL_SWITCH_SX}
                  inputProps={{ 'aria-label': 'polymarket maker enabled' }}
                  checked={polySwitch.checked}
                  disabled={polySwitch.disabled}
                  onChange={(_, v) => void doSetMakerFlag(polyFlag, v)}
                />
              </span>
            </Tooltip>
          </TableCell>
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            <Tooltip
              title={UNGROUPED_ACTIVATION_TOOLTIP}
              placement="top"
              disableHoverListener={!kalshiActivationBlocked}
            >
              <span>
                <Switch
                  size="small"
                  sx={CTRL_SWITCH_SX}
                  inputProps={{ 'aria-label': 'kalshi maker enabled' }}
                  checked={kalshiSwitch.checked}
                  disabled={kalshiSwitch.disabled}
                  onChange={(_, v) => void doSetMakerFlag(kalshiFlag, v)}
                />
              </span>
            </Tooltip>
          </TableCell>
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            {anyRule && (anyRule.balancingContractsRemaining ?? 0) > 0 ? (
              <CircularProgress size={14} />
            ) : togglingTT ? (
              <CircularProgress size={14} />
            ) : (
              <Switch
                size="small"
                sx={CTRL_SWITCH_SX}
                inputProps={{ 'aria-label': 'taker-taker enabled' }}
                checked={ttOn}
                onChange={(_, v) => void handleTTToggle(v)}
              />
            )}
          </TableCell>
        </TableRow>
      </>
    );
  },
  (prev, next) =>
    // `dataIndex` must be compared so the row re-renders (and re-stamps its
    // `data-index`) when a sort reorders it — react-virtual keys measured
    // heights off that attribute. `measureRef` is a stable callback.
    prev.dataIndex === next.dataIndex &&
    prev.path === next.path &&
    prev.pair === next.pair &&
    prev.rule === next.rule &&
    prev.anyRule === next.anyRule &&
    // `liveState` is no longer a prop — the row reads it reactively via
    // `useLiveState(store)`, so a price tick re-renders ONLY the affected
    // row without flowing through this memo. `store` is a stable instance.
    prev.store === next.store &&
    // Identity compare is enough BECAUSE `useVenuePositionsStatusByScope` mints
    // one object per scope and only when something the three venue cells render
    // changes — the per-poll `lastSuccessAt` heartbeat does not, so a healthy
    // sweep costs no row renders, while a flip to blocked / stale / fresh
    // repaints every row.
    prev.venueStatus === next.venueStatus &&
    prev.selected === next.selected &&
    prev.onToggleSelect === next.onToggleSelect &&
    prev.onCreateRule === next.onCreateRule &&
    prev.onSetMakerFlag === next.onSetMakerFlag &&
    prev.onUpdateRule === next.onUpdateRule &&
    prev.onHidePair === next.onHidePair
);
